import { lazy, Suspense, useState, useEffect, useRef, useCallback, useMemo, startTransition, type ComponentProps } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import LoginPage from './components/pages/LoginPage';
import BetaRegistrationPage from './components/pages/BetaRegistrationPage';
import AuthCallbackPage from './components/pages/AuthCallbackPage';
import BetaFeedbackWidget from './components/feedback/BetaFeedbackWidget';
import type { PageName, Trade } from './types';
import HorizontalMenu from './components/navigation/HorizontalMenu';
import LayoutPresetsMenu from './components/ui/LayoutPresetsMenu';
import { Sidebar } from './components/navigation/Sidebar';
import CreateCustomEntryModal from './components/ui/CreateCustomEntryModal';
import GlobalToast from './components/ui/GlobalToast';
import DashboardPage from './components/pages/DashboardPage';
import WorkspaceLoadGuard from './components/ui/WorkspaceLoadGuard';
import { WorkspaceViewportGate } from './components/ui/WorkspaceViewportGate';
import WorkspaceTransitionSurface from './components/ui/WorkspaceTransitionSurface';
import { CalendarPage } from './components/pages/CalendarPage';
const Trend = lazy(() => import('./components/pages/Trend'));
const PerformanceOverview = lazy(() => import('./components/pages/PerformanceOverview'));
const WinVsLossDays = lazy(() => import('./components/pages/WinVsLoss'));
const TagPatterns = lazy(() => import('./components/pages/TagPatterns'));
import { TradesPage } from './components/pages/TradesPage';
import { JournalPage } from './components/pages/JournalPage';
import { SearchPage } from './components/pages/SearchPage';
import NewTradePage from './components/pages/NewTradePage';
import { JournalEntryPage } from './components/pages/JournalEntryPage';
import * as SettingsPageModule from './components/pages/SettingsPage';
import type { MarketType } from './components/pages/AssetClassSelector';
import { readDefaultAnalyticsAssetClass } from './config/tradingSettingsDefaults';
import SupportPage from './components/pages/SupportPage';
const SupportTicketsPage = lazy(() => import('./components/pages/SupportTicketsPage'));
const ImportTradesPage = lazy(() => import('./components/pages/ImportTradesPage'));
const RejectedImportsPage = lazy(() => import('./components/pages/RejectedImportsPage'));
import TradeDetailsPage from './components/pages/TradeDetails';
import { OnboardingTour } from './components/ui/OnboardingChecklist';
import AdminToolsPage from './components/pages/AdminToolsPage';
import Challenges from './components/pages/Challenges';
const BusinessPage = lazy(() => import('./components/pages/BusinessPage'));
import BillingAccessGate from './components/billing/BillingAccessGate';
import { fetchBillingStatus, fetchCheckoutReturnBillingStatus, createCheckoutSession, type BillingAccessState } from './lib/billing';
import { AbandonedCheckoutPage } from './components/pages/AbandonedCheckoutPage';
import { ReactivateSubscriptionPage } from './components/pages/ReactivateSubscriptionPage';
import { getBranding } from './config/branding';
import { getErrorMessage, isAbortLikeError, isAbortLikeMessage } from './lib/requestAbort.ts';
import { resolveTenantProductName } from './lib/whiteLabelBranding';
import { getAppLogoSrc } from './constants/branding';
import { fetchAdminBillingAccess, fetchBusinessAdminAccess, type AdminBillingAccessState, type BusinessAdminAccessState } from './lib/adminTools';
import { fetchPublicRegistrationState, fetchPublicTenantBranding, fetchWhiteLabelBranding, isBaseTradeProofHostname } from './lib/whiteLabel';
import {
  applyNeutralWhiteLabelBrandingToDocument,
  applyWhiteLabelBrandingToDocument,
  buildWhiteLabelAppBranding,
  getCachedWhiteLabelAppBranding,
  setCachedWhiteLabelAppBranding,
  type WhiteLabelAppBranding,
} from './lib/whiteLabelBranding';
import {
  getTenantDisplayAssetClassesFromLaunchControls,
  setActiveTenantSlugForDisplayScope,
  setTenantDisplayAssetClasses,
} from './lib/trades/tenantTradeScope';
import { fetchReplayLaunchControlsStatus } from './lib/replay/launchControls';
import { bootstrapTenantMembership } from './lib/tenancy/bootstrap';
import { activatePrivateTradingScope, getVerifiedPrivateTradingScope, invalidatePrivateTradingScope, privateTradingScopeChangedEventName, readPrivateTradingJson, writePrivateTradingJson } from './lib/storage/privateTradingStorage';
import { clearAuthoritativeTenantSlug, setAuthoritativeTenantSlug, useAuthoritativeTenantSlug } from './lib/tenantRequestContext';
import { BILLING_BYPASS_ENABLED as BILLING_BYPASS_GATE } from './config/runtime/billingBypass';
import { isChallengeAdmin } from './lib/tenantPermissions';
import PublicSiteShell from './components/public/PublicSiteShell';
import TenantHostStatusPage from './components/public/TenantHostStatusPage';
import { isPublicShellRoutePath, normalizePublicPath, type PublicRoutePath } from './components/public/publicRoutes';
import { classifyTenantHostname, isTenantHostMarketingPath } from './lib/tenancy/tenantHostRouting';
import { DemoTradesOverrideProvider, enableDemoMode, useDemoModeState, shouldUseDemoTrades, disableDemoMode, isDemoModeTemporarilyDisabled, setDemoModeTemporarilyDisabled, readPublicGuestSession, enablePublicGuestSession, clearPublicGuestSession, usePublicGuestSession } from './demo/demoMode';
import { GuestSignupModal } from './components/ui/GuestSignupModal';
import { GuestModeHeaderIndicator } from './components/ui/GuestModeHeaderIndicator';
import { ExitDemoConfirmationModal } from './components/ui/ExitDemoConfirmationModal';
import { fetchDemoModePreference, saveDemoModePreference } from './demo/demoPreferences';
import { getDashboardDemoTradesForTenant } from './demo/demoData';
import { hasCanonicalDashboardTrades } from './lib/trades/dashboardTradeSource';
import { currentRuntime } from './config/runtime';
import { useAuthoritativeTradesRecords } from './hooks/useAuthoritativeTrades';
import { getPersistedTradeHistoryStatus, requestPersistedTradeHistoryRetry, subscribePersistedTradeHistoryStatus } from './lib/import-engine/persistedCsvTradeRead';
import { authDebugLog, clearLoginFlowActive, clearLogoutTransitionPending, clearStoredLoginAuthError, clearStoredLoginSuccessMessage, getCurrentSession, getLoginFlowActive, getLogoutTransitionPending, getStoredAppIdentity, getStoredLoginAuthError, getStoredLoginSuccessMessage, onSessionChange, sanitizeLoginAuthErrorMessage, sessionToAppIdentity, setLoginFlowActive, setLogoutTransitionPending, setStoredLoginAuthError, signOut } from './lib/auth.ts';
import { calculateBootstrapProgress } from './lib/bootstrapProgress';
import ForgotPasswordPage from './components/pages/ForgotPasswordPage';
import { recordDemoLoginSuccess, recordOutreachLinkVisit, retryPendingDemoLoginSuccess } from './lib/outreachTracking';
import ResetPasswordPage from './components/pages/ResetPasswordPage';
import PaymentSuccessModal from './components/ui/PaymentSuccessModal';
import { Error404 } from './components/ui/ProductionErrorPages';
const DASHBOARD_PATHNAME = '/dashboard';
const ADMIN_TOOLS_PATHNAME = '/admin/tools';
const TRADE_DETAILS_BACK_TARGET_KEY = 'tt_trade_details_back_target';

const SettingsPage = ((SettingsPageModule as any).default ?? (SettingsPageModule as any).SettingsPage) as any;
const FIRST_SESSION_ONBOARDING_REQUESTED_KEY = 'tradeproof:first-session-onboarding-requested.v1';
const FIRST_SESSION_ONBOARDING_COMPLETED_KEY_BASE = 'tradeproof:first-session-onboarding-completed.v1';

const readFirstSessionOnboardingRequested = () => {
  try {
    return sessionStorage.getItem(FIRST_SESSION_ONBOARDING_REQUESTED_KEY) === 'true';
  } catch {
    return false;
  }
};

const getFirstSessionOnboardingCompletedKey = (userKey: string) => `${FIRST_SESSION_ONBOARDING_COMPLETED_KEY_BASE}:${userKey}`;

type CheckoutReturnState = {
  visible: boolean;
  status: 'idle' | 'pending' | 'confirmed' | 'failed';
  plan: 'monthly' | 'yearly' | '';
  attempts: number;
  failureReason: 'session' | 'billing' | null;
};

type CheckoutReturnSessionRecoveryState = {
  attempts: number;
  failed: boolean;
};

type BillingNoticeState = {
  message: string;
};

const BILLING_BYPASS_ENABLED = BILLING_BYPASS_GATE; // dev-only bypass, see runtime/billingBypass.ts
const BILLING_BYPASS_ACTIVE = BILLING_BYPASS_ENABLED;
const BILLING_BYPASS_OWNER_UID = '__billing_bypass__';
const BILLING_BYPASS_STATE: BillingAccessState = {
  ok: true,
  configured: true,
  hasAccess: true,
  subscriptionStatus: 'active',
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  customerPortalEligible: false,
  checkoutEligible: false,
  plan: {
    name: 'Trade-Proof',
    interval: 'month',
  },
};

const hasConfirmedBillingAccess = (state: BillingAccessState | null | undefined) => Boolean(
  state?.hasAccess === true || state?.isAdminBillingBypass === true
);

const getTimezoneFallbackLabel = () => {
  try {
    const timeZone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').trim();
    if (!timeZone) return 'UTC';
    return (timeZone.includes('/') ? (timeZone.split('/').filter(Boolean).pop() || timeZone) : timeZone).replace(/\//g, '_');
  } catch {}
  return 'UTC';
};

type PublicLocationState = {
  path: PublicRoutePath | null;
  hash: string | null;
  explicit?: boolean;
};

type LoginAuthErrorState = {
  message: string;
  nonce: number;
};

type LoginSuccessState = {
  message: string;
  nonce: number;
};

type SignupCheckoutHandoffRecord = {
  stage: 'preparing' | 'redirecting';
  startedAt: number;
  redirectStartedAt: number | null;
};

const LOGIN_RESET_SUCCESS_MESSAGE = 'Your password has been updated. You can now sign in.';
const SESSION_EXPIRED_LOGIN_MESSAGE = 'Your session expired. Please sign in again.';

const LOGOUT_TRANSITION_RELEASE_DELAY_MS = 96;
const CHECKOUT_RETURN_SESSION_RECOVERY_RETRY_MS = 400;
const CHECKOUT_RETURN_SESSION_RECOVERY_MAX_ATTEMPTS = 30;
const CHECKOUT_RETURN_BILLING_RETRY_MS = 800;
const CHECKOUT_RETURN_BILLING_MAX_ATTEMPTS = 20;
const WORKSPACE_SLOW_THRESHOLD_MS = Number(
  import.meta.env?.VITE_WORKSPACE_SLOW_THRESHOLD_MS
  || import.meta.env?.VITE_WORKSPACE_LOAD_TIMEOUT_MS
  || 8000,
);

function hasCheckoutReturnSessionId() {
  if (typeof window === 'undefined') return false;
  try {
    return !!new URLSearchParams(window.location.search || '').get('session_id');
  } catch {
    return false;
  }
}

function resolveStoredIdentityFallback() {
  if (!import.meta.env.DEV) return null;
  if (getLogoutTransitionPending()) return null;
  return getStoredAppIdentity();
}

const readNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const roundDurationMs = (value: number | null) => value == null ? null : Number(value.toFixed(1));
const createBootstrapCorrelationId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {}
  return `bootstrap-${Math.random().toString(36).slice(2, 12)}`;
};
const SIGNUP_CHECKOUT_HANDOFF_KEY = 'tt_signup_checkout_handoff_active';
const isStripeBillingReturnKind = (value: string) => value === 'success' || value === 'cancelled' || value === 'canceled';
const readBillingReturnKindFromLocation = () => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const billing = String(params.get('billing') || '').trim().toLowerCase();
    return isStripeBillingReturnKind(billing) ? billing : '';
  } catch {
    return '';
  }
};
const readSignupCheckoutHandoffRecord = (): SignupCheckoutHandoffRecord | null => {
  try {
    const raw = sessionStorage.getItem(SIGNUP_CHECKOUT_HANDOFF_KEY);
    if (!raw) return null;
    if (raw === 'true') {
      return {
        stage: 'preparing',
        startedAt: 0,
        redirectStartedAt: null,
      };
    }

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.active === false) return null;

    return {
      stage: parsed.stage === 'redirecting' ? 'redirecting' : 'preparing',
      startedAt: Number(parsed.startedAt || 0),
      redirectStartedAt: parsed.redirectStartedAt == null ? null : Number(parsed.redirectStartedAt),
    };
  } catch {
    return null;
  }
};
const writeSignupCheckoutHandoffRecord = (record: SignupCheckoutHandoffRecord) => {
  try {
    sessionStorage.setItem(SIGNUP_CHECKOUT_HANDOFF_KEY, JSON.stringify({
      active: true,
      stage: record.stage,
      startedAt: record.startedAt,
      redirectStartedAt: record.redirectStartedAt,
    }));
  } catch {}
};
const shouldRecoverSignupCheckoutHandoffOnReload = () => {
  if (readBillingReturnKindFromLocation()) return false;
  if (!readSignupCheckoutHandoffRecord()) return false;
  try {
    const entries = typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
      ? performance.getEntriesByType('navigation')
      : [];
    const navigationEntry = entries[0] as { type?: string } | undefined;
    return String(navigationEntry?.type || '') === 'reload';
  } catch {
    return false;
  }
};
const readSignupCheckoutHandoffActive = () => {
  return !!readSignupCheckoutHandoffRecord();
};
const setSignupCheckoutHandoffActive = () => {
  writeSignupCheckoutHandoffRecord({
    stage: 'preparing',
    startedAt: Date.now(),
    redirectStartedAt: null,
  });
};
const markSignupCheckoutHandoffRedirectStarted = () => {
  const existing = readSignupCheckoutHandoffRecord();
  writeSignupCheckoutHandoffRecord({
    stage: 'redirecting',
    startedAt: existing?.startedAt || Date.now(),
    redirectStartedAt: Date.now(),
  });
};
const clearSignupCheckoutHandoffActive = () => {
  try {
    sessionStorage.removeItem(SIGNUP_CHECKOUT_HANDOFF_KEY);
  } catch {}
};
const tpStripeFlashLog = (event: string, payload: Record<string, unknown> = {}) => {
  try {
    const monoTs = typeof performance !== 'undefined' ? performance.now() : null;
    console.info('[TP-STRIPE-FLASH]', event, { ts: Date.now(), monoTs, ...payload });
  } catch {}
};

const buildBillingReturnLoginLocation = () => {
  try {
    const url = new URL(window.location.href);
    url.pathname = '/login';
    return {
      href: `${url.pathname}${url.search}${url.hash}`,
      path: '/login' as PublicRoutePath,
      hash: String(url.hash || '').replace(/^#/, '').trim() || null,
    };
  } catch {
    return {
      href: '/login',
      path: '/login' as PublicRoutePath,
      hash: null,
    };
  }
};

const normalizeAuthenticatedShellPath = (value: string) => {
  const trimmed = String(value || '/').trim() || '/';
  if (trimmed.length <= 1) {
    return trimmed;
  }
  return trimmed.replace(/\/+$/, '') || '/';
};

const AUTHENTICATED_ROUTE_PAGE_BY_PATH: Partial<Record<string, PageName>> = {
  '/dashboard': 'Dashboard',
  '/trades': 'Trades',
  '/trades/details': 'Trade Details',
  '/settings': 'Settings',
  '/journal': 'Journal',
  '/import': 'Import Trades',
  '/search': 'Search',
  '/support': 'Support',
  '/calendar': 'Calendar',
  '/reports': 'Reports',
  '/reports/performance': 'Performance Overview',
  '/reports/win-vs-loss': 'Win vs Loss Days',
  '/reports/tag-patterns': 'Tag Patterns',
  '/trades/new': 'New Trade',
  '/import/rejections': 'Import Rejections',
  '/admin': 'Admin Tools',
  '/admin/tools': 'Admin Tools',
  '/admin/support-tickets': 'Support Tickets',
  '/challenges': 'Challenges',
  '/business': 'Business',
};

const AUTHENTICATED_ROUTE_PATH_BY_PAGE: Partial<Record<PageName, string>> = {
  Dashboard: '/dashboard',
  Trades: '/trades',
  'Trade Details': '/trades/details',
  Settings: '/settings',
  Journal: '/journal',
  'Import Trades': '/import',
  Search: '/search',
  Support: '/support',
  Calendar: '/calendar',
  Reports: '/reports',
  'Performance Overview': '/reports/performance',
  'Win vs Loss Days': '/reports/win-vs-loss',
  'Tag Patterns': '/reports/tag-patterns',
  'New Trade': '/trades/new',
  'Import Rejections': '/import/rejections',
  'Admin Tools': '/admin/tools',
  'Support Tickets': '/admin/support-tickets',
  Challenges: '/challenges',
  Business: '/business',
};

const resolveAuthenticatedPageFromPath = (pathname: string): PageName | null => {
  const normalizedPath = normalizeAuthenticatedShellPath(pathname);
  return AUTHENTICATED_ROUTE_PAGE_BY_PATH[normalizedPath] || null;
};

const resolveAuthenticatedPathForPage = (page: PageName): string | null => {
  return AUTHENTICATED_ROUTE_PATH_BY_PAGE[page] || null;
};

const LEGAL_ROUTE_HASH_BY_PATH: Record<string, 'terms' | 'privacy'> = {
  '/terms': 'terms',
  '/terms-of-service': 'terms',
  '/privacy': 'privacy',
  '/privacy-policy': 'privacy',
};

const readInitialActivePage = (): PageName => {
  try {
    return resolveAuthenticatedPageFromPath(String(window.location.pathname || '/').trim() || '/') ?? 'Dashboard';
  } catch {
    return 'Dashboard';
  }
};

function isAbandonedCheckoutState(state: BillingAccessState | null): boolean {
  if (!state) return false;
  if (state.trialUsedAt || state.trial_used_at) return false;
  const normalizedStatus = String(state.subscriptionStatus || state.status || '').trim().toLowerCase().replace(/[_\s]+/g, ' ');
  const hasConfirmedHistory = Boolean(state.stripeSubscriptionId) && !['', 'inactive', 'incomplete', 'incomplete expired'].includes(normalizedStatus);
  return !hasConfirmedHistory;
}

function isReactivationState(state: BillingAccessState | null): boolean {
  if (!state || state.hasAccess) return false;
  return !isAbandonedCheckoutState(state);
}

export default function App() {
  const recoverSignupCheckoutHandoffOnLoadRef = useRef<boolean>(shouldRecoverSignupCheckoutHandoffOnReload());
  const cleanLoginEscapeSignatureRef = useRef<string | null>(null);
  const demoMode = useDemoModeState();
  const showDemoUi = true;
  const authoritativeTrades = useAuthoritativeTradesRecords();
  const [tradeHistoryStatus, setTradeHistoryStatus] = useState(getPersistedTradeHistoryStatus);
  useEffect(() => subscribePersistedTradeHistoryStatus(() => setTradeHistoryStatus(getPersistedTradeHistoryStatus())), []);
  const [firstSessionOnboardingRequested, setFirstSessionOnboardingRequested] = useState(() => readFirstSessionOnboardingRequested());
  const [demoModePermanentlyDisabled, setDemoModePermanentlyDisabled] = useState(false);
  const [demoModeTemporarilyDisabled, setDemoModeTemporarilyDisabledState] = useState(() => isDemoModeTemporarilyDisabled());
  const [isExitDemoModalOpen, setIsExitDemoModalOpen] = useState(false);
  const [isGuestSignupModalOpen, setIsGuestSignupModalOpen] = useState(false);
  const [guestModalFeatureName, setGuestModalFeatureName] = useState<string | undefined>(undefined);
  const isPublicGuestSessionActive = usePublicGuestSession();
  const isDemoRoute = (() => {
    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      return pathname === '/demo' || pathname === '/demo/' || pathname.startsWith('/demo/');
    } catch {
      return false;
    }
  })();
  const isPublicGuest = isPublicGuestSessionActive || isDemoRoute;

  const triggerGuestRestrictedAction = useCallback((featureName?: string) => {
    setGuestModalFeatureName(featureName);
    setIsGuestSignupModalOpen(true);
  }, []);

  useEffect(() => {
    const handleGlobalGuestRestrictedAction = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      triggerGuestRestrictedAction(detail?.featureName);
    };
    window.addEventListener('tradeproof:guest-restricted-action', handleGlobalGuestRestrictedAction);
    return () => window.removeEventListener('tradeproof:guest-restricted-action', handleGlobalGuestRestrictedAction);
  }, [triggerGuestRestrictedAction]);
  const initialLoginSuccessState = (() => {
    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const publicPath = normalizePublicPath(pathname);
      const params = new URLSearchParams(window.location.search || '');
      if ((publicPath === ('/login' as PublicRoutePath) || publicPath === ('/signup' as PublicRoutePath)) && params.get('reset') === 'success') {
        return {
          message: LOGIN_RESET_SUCCESS_MESSAGE,
          nonce: Date.now(),
        };
      }
    } catch {}
    return null;
  })();
  const readPublicLocation = (): PublicLocationState => {
    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';

      if (pathname === '/demo' || pathname === '/demo/' || pathname.startsWith('/demo/')) {
        enablePublicGuestSession();
        window.history.replaceState({}, '', '/dashboard');
        return { path: null, hash: null };
      }

      const trimmedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
      const hash = String(window.location.hash || '').replace(/^#/, '').trim() || null;
      return {
        path: normalizePublicPath(trimmedPathname),
        hash: hash || LEGAL_ROUTE_HASH_BY_PATH[trimmedPathname] || null,
      };
    } catch {
      return { path: '/', hash: null };
    }
  };

  const initialCheckoutReturnState: CheckoutReturnState = (() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const billing = String(params.get('billing') || '').trim().toLowerCase();
      const plan = String(params.get('plan') || '').trim().toLowerCase();
      const normalizedPlan: CheckoutReturnState['plan'] = (plan === 'monthly' || plan === 'yearly') ? (plan as CheckoutReturnState['plan']) : '';

      if (billing === 'success') {
        return {
          visible: true,
          status: 'pending' as const,
          plan: normalizedPlan,
          attempts: 0,
          failureReason: null,
        };
      }

      return {
        visible: false,
        status: 'idle' as const,
        plan: normalizedPlan,
        attempts: 0,
        failureReason: null,
      };
    } catch {
      return {
        visible: false,
        status: 'idle' as const,
        plan: '',
        attempts: 0,
        failureReason: null,
      };
    }
  })();

  const initialBillingNoticeState = (() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const billing = String(params.get('billing') || '').trim().toLowerCase();
      if (billing === 'cancelled' || billing === 'canceled') {
        return {
          message: 'Checkout was cancelled. You can restart your free trial anytime.',
        };
      }
    } catch {}
    return null;
  })();

  const [user, setUser] = useState<any>(null);
  const [authReady, setAuthReady] = useState<boolean>(false);
  // Seeded synchronously from the branding cache so the workspace loading screen
  // can render the correct tenant logo on its FIRST paint (reloads / re-renders)
  // instead of waiting for the async branding fetch to resolve.
  const [whiteLabelAppBranding, setWhiteLabelAppBranding] = useState<WhiteLabelAppBranding | null>(
    () => getCachedWhiteLabelAppBranding(),
  );
  const [publicTenantState, setPublicTenantState] = useState<'live' | 'draft' | 'suspended' | 'not_found' | null>(null);
  const [publicRegistrationEnabled, setPublicRegistrationEnabled] = useState<boolean | null>(null);
  const publicBrandingAppliedRef = useRef(false);
  const [authBootstrapError, setAuthBootstrapError] = useState<string | null>(null);
  const [authBootstrapAttempt, setAuthBootstrapAttempt] = useState(0);
  const [tenantBootstrapReady, setTenantBootstrapReady] = useState<boolean>(false);
  const [tenantBootstrapSlow, setTenantBootstrapSlow] = useState<boolean>(false);
  const [tenantMembershipRole, setTenantMembershipRole] = useState<string | null>(null);
  const tenantBootstrapTimeoutRef = useRef<number | null>(null);
  const effectiveTenantBootstrapReady = tenantBootstrapReady;
  const WORKSPACE_LOAD_TIMEOUT_MS = Number(import.meta.env?.VITE_WORKSPACE_LOAD_TIMEOUT_MS || 8000);
  const [tenantBootstrapError, setTenantBootstrapError] = useState<string | null>(null);
  const [dashboardCommitted, setDashboardCommitted] = useState<boolean>(false);
  const hasNavigatedRef = useRef<boolean>(false);
  const [loginAuthErrorState, setLoginAuthErrorState] = useState<LoginAuthErrorState | null>(() => {
    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const publicPath = normalizePublicPath(pathname);
      if (publicPath === ('/signup' as PublicRoutePath)) {
        clearStoredLoginAuthError();
        return null;
      }
      return getStoredLoginAuthError();
    } catch {
      return null;
    }
  });
  const setLoginAuthError = (val: LoginAuthErrorState | null) => {
    setLoginAuthErrorState(val);
  };
  const loginAuthError = loginAuthErrorState;
  const [loginSuccessMessage, setLoginSuccessMessage] = useState<LoginSuccessState | null>(() => initialLoginSuccessState);
  const [authIntentPending, setAuthIntentPendingState] = useState<boolean>(false);
  const [tenantBootstrapAttempt, setTenantBootstrapAttempt] = useState(0);
  const [publicLocation, setPublicLocation] = useState<PublicLocationState>(() => readPublicLocation());
  const [checkoutReturnSessionRecovery, setCheckoutReturnSessionRecovery] = useState<CheckoutReturnSessionRecoveryState>({
    attempts: 0,
    failed: false,
  });
  const readStoredNavLayout = (): 'sidebar' | 'top' => {
    try {
      const raw = localStorage.getItem('tradeproof:navigation-layout');
      if (raw === 'sidebar' || raw === 'top') return raw;
    } catch {}
    return 'top';
  };
  const [collapsed, setCollapsed] = useState(false);
  const [navLayout, setNavLayout] = useState<'sidebar' | 'top'>(() => readStoredNavLayout());
  const [navigationLayoutMode, setNavigationLayoutMode] = useState<'both' | 'side_only' | 'top_only'>('both');
  const [active, setActive] = useState<PageName>(() => readInitialActivePage());
  const [settingsInitialTabLabel, setSettingsInitialTabLabel] = useState<string | undefined>(undefined);
  const isSettingsRoute = active === 'Settings';
  const isAnalyticsRoute = (['Reports', 'Performance Overview', 'Win vs Loss Days', 'Tag Patterns'] as string[]).includes(active);
  const isSupportRoute = active === 'Support';
  const isDashboardRoute = active === 'Dashboard';
  const isImportTradesRoute = active === 'Import Trades';
  const shellHorizontalPaddingClassName = (isSettingsRoute || isSupportRoute || isImportTradesRoute || active === 'Search' || active === 'New Trade' || active === 'Journal')
    ? 'px-0'
    : 'px-4 sm:px-6 lg:px-8';
  const sideNavTopSpacingClassName = navLayout === 'sidebar' && (active === 'Calendar' || active === 'Trades') ? 'pt-3' : 'pt-0';
  const sidePanelContentShellClassName = active === 'Journal'
    ? 'w-full min-w-0 pb-0'
    : `w-full min-w-0 ${navLayout === 'sidebar' ? 'pt-0' : 'pt-8'} pb-10`;
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>(() => readDefaultAnalyticsAssetClass() as MarketType);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Initialize theme from localStorage to prevent flash
    try {
      const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
      if (savedTheme) return savedTheme;

      // White-label tenants use the branded dark presentation on first visit.
      // The existing persisted preference still wins above, and the normal
      // theme toggle continues to persist future user choices.
      if (classifyTenantHostname(window.location.hostname) !== 'base') return 'dark';

      // Fallback to system preference
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const [tenantThemeMode, setTenantThemeMode] = useState<'light_only' | 'dark_only' | 'light_dark'>('light_dark');
  const [viewingDate, setViewingDate] = useState<Date | null>(null);
  const [isNewEntry, setIsNewEntry] = useState(false);
  const [createCustomOpen, setCreateCustomOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  useEffect(() => {
    const clearSelectedTrade = () => setSelectedTrade(null);
    window.addEventListener(privateTradingScopeChangedEventName(), clearSelectedTrade);
    return () => window.removeEventListener(privateTradingScopeChangedEventName(), clearSelectedTrade);
  }, []);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [billingState, setBillingState] = useState<BillingAccessState | null>(() => (BILLING_BYPASS_ACTIVE ? BILLING_BYPASS_STATE : null));
  const [billingStateOwnerUid, setBillingStateOwnerUid] = useState<string | null>(() => (BILLING_BYPASS_ACTIVE ? BILLING_BYPASS_OWNER_UID : null));
  const authoritativeTenantSlug = useAuthoritativeTenantSlug();
  const demoTrades = useMemo(() => getDashboardDemoTradesForTenant(authoritativeTenantSlug || currentRuntime.tenant.id), [authoritativeTenantSlug, currentRuntime.tenant.id]);
  const isDemoOrDisabled = demoModePermanentlyDisabled || demoModeTemporarilyDisabled;
  const demoTradesActive = shouldUseDemoTrades({
    isDemoMode: demoMode.enabled,
    accountType: isDemoOrDisabled ? null : ((billingState?.isAdminBillingBypass === true || BILLING_BYPASS_ACTIVE) ? 'bypass' : null),
    hasRealTrades: tradeHistoryStatus.phase !== 'complete' || hasCanonicalDashboardTrades(authoritativeTrades),
  });
  const [billingReady, setBillingReady] = useState(BILLING_BYPASS_ACTIVE);
  const [abandonedCheckoutLoading, setAbandonedCheckoutLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [adminToolsAccess, setAdminToolsAccess] = useState<AdminBillingAccessState | null>(null);
  const [adminToolsAccessReady, setAdminToolsAccessReady] = useState(false);
  const [adminToolsAccessError, setAdminToolsAccessError] = useState<string | null>(null);
  const [businessAccess, setBusinessAccess] = useState<BusinessAdminAccessState | null>(null);
  const [businessAccessReady, setBusinessAccessReady] = useState(false);
  const showAdminToolsNav = Boolean(
    adminToolsAccessReady
    && (adminToolsAccess?.canAccessAdminTab || adminToolsAccess?.isAdmin),
  );
  const activeTenantId = authoritativeTenantSlug || currentRuntime.tenant.id;
  const showBusinessNav = Boolean(businessAccessReady && businessAccess?.canAccessBusiness);
  const canManageChallengeVideos = Boolean(
    showAdminToolsNav
    || (tenantBootstrapReady && isChallengeAdmin(tenantMembershipRole)),
  );
  const [signupCheckoutHandoffActive, setSignupCheckoutHandoffActiveState] = useState<boolean>(() => (
    recoverSignupCheckoutHandoffOnLoadRef.current
      ? false
      : readSignupCheckoutHandoffActive()
  ));
  const [checkoutReturn, setCheckoutReturn] = useState<CheckoutReturnState>(initialCheckoutReturnState);
  const [billingNotice, setBillingNotice] = useState<BillingNoticeState | null>(initialBillingNoticeState);
  const [loginFlowActive, setLoginFlowActiveState] = useState<boolean>(() => (
    recoverSignupCheckoutHandoffOnLoadRef.current
      ? false
      : getLoginFlowActive()
  ));
  const [logoutTransitionPending, setLogoutTransitionPendingState] = useState<boolean>(() => (
    getLogoutTransitionPending()
  ));
  const isLogoutTransitionActive = logoutTransitionPending || getLogoutTransitionPending();
  const tenantBootstrapKeyRef = useRef<string | null>(null);
  const tenantBootstrapSlugRef = useRef<string | null>(null);
  const authReadyForBootstrapAtRef = useRef<number | null>(null);
  const bootstrapStartedAtRef = useRef<number | null>(null);
  const bootstrapCorrelationIdRef = useRef<string | null>(null);
  const bootstrapCorrelationAttemptRef = useRef<number | null>(null);
  const deniedResponseAtRef = useRef<number | null>(null);
  const deniedTimingsRef = useRef<{ sessionLookupMs: number; bootstrapDurationMs: number; totalMs: number } | null>(null);
  const lastLoggedDeniedNonceRef = useRef<number | null>(null);
  const previousAuthReadyRef = useRef<boolean | null>(null);
  const previousUserUidRef = useRef<string | null>(null);
  const previousTenantBootstrapReadyRef = useRef<boolean | null>(null);
  const previousRenderBranchRef = useRef<string | null>(null);
  const checkoutReturnNavigationCommittedRef = useRef(false);
  const logoutTransitionReleaseTimerRef = useRef<number | null>(null);
  const adminToolsAccessRequestIdRef = useRef(0);
  const billingAccessRequestIdRef = useRef(0);
  const layoutPresetsButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void recordOutreachLinkVisit();
  }, []);

  useEffect(() => {
    if (!authReady || !user?.uid) return;
    void getCurrentSession().then((session) => retryPendingDemoLoginSuccess(session));
  }, [authReady, user?.uid]);

  useEffect(() => {
    const contentShell = mainRef.current;
    if (!contentShell || typeof window === 'undefined') return;

    const frame = window.requestAnimationFrame(() => {
      let source: HTMLElement | null = contentShell;
      let backgroundColor = '';
      while (source) {
        const candidate = window.getComputedStyle(source).backgroundColor;
        if (candidate && candidate !== 'transparent' && candidate !== 'rgba(0, 0, 0, 0)') {
          backgroundColor = candidate;
          break;
        }
        source = source.parentElement;
      }
      contentShell.style.setProperty('--page-surface-bg', backgroundColor || 'hsl(var(--background))');
    });

    return () => window.cancelAnimationFrame(frame);
  }, [active, theme]);


  const dashboardUserKey = (() => {
    try {
      return String(user?.uid || 'guest');
    } catch {
      return 'guest';
    }
  })();
  const firstSessionOnboardingCompletedKey = getFirstSessionOnboardingCompletedKey(dashboardUserKey);
  const firstSessionOnboardingCompleted = (() => {
    try {
      return localStorage.getItem(firstSessionOnboardingCompletedKey) === 'true';
    } catch {
      return false;
    }
  })();
  const firstSessionOnboardingVisible = active === 'Dashboard'
    && firstSessionOnboardingRequested
    && !firstSessionOnboardingCompleted
    && tradeHistoryStatus.phase === 'complete'
    && authoritativeTrades.length === 0
    && !demoModePermanentlyDisabled;

  const setDeniedLoginAuthError = useCallback((message: string) => {
    const safeMessage = sanitizeLoginAuthErrorMessage(message) || 'Access denied.';
    const storedError = setStoredLoginAuthError(message);
    setLoginAuthError(storedError || {
      message: safeMessage,
      nonce: Date.now(),
    });
  }, []);

  const clearLoginSuccessNotice = useCallback(() => {
    clearStoredLoginSuccessMessage();
    setLoginSuccessMessage(null);
  }, []);

  const clearBillingNotice = useCallback(() => {
    setBillingNotice(null);
  }, []);

  const clearLoginAuthErrorNotice = useCallback(() => {
    clearStoredLoginAuthError();
    setLoginAuthError(null);
  }, []);

  const clearLogoutTransitionNotice = useCallback(() => {
    setLogoutTransitionPendingState(false);
    clearLogoutTransitionPending();
  }, []);

  const beginLogoutTransition = useCallback(() => {
    if (logoutTransitionReleaseTimerRef.current != null) {
      window.clearTimeout(logoutTransitionReleaseTimerRef.current);
      logoutTransitionReleaseTimerRef.current = null;
    }
    setAuthIntentPendingState(false);
    setLoginFlowActiveState(false);
    clearLoginFlowActive();
    checkoutReturnNavigationCommittedRef.current = false;
    setLogoutTransitionPendingState(true);
    setLogoutTransitionPending();
    try {
      window.history.replaceState({}, '', '/login');
      setPublicLocation({ path: '/login', hash: null });
    } catch {}
  }, [setPublicLocation]);

  useEffect(() => {
    if (!logoutTransitionPending && logoutTransitionReleaseTimerRef.current != null) {
      window.clearTimeout(logoutTransitionReleaseTimerRef.current);
      logoutTransitionReleaseTimerRef.current = null;
    }
    if (!logoutTransitionPending) return;
    if (!authReady || user?.uid) return;

    const liveLoginPath = (() => {
      try {
        return normalizePublicPath(String(window.location.pathname || '/').trim() || '/') === ('/login' as PublicRoutePath);
      } catch {
        return false;
      }
    })();

    if (publicLocation.path !== ('/login' as PublicRoutePath) && !liveLoginPath) return;

    if (logoutTransitionReleaseTimerRef.current != null) return;

    logoutTransitionReleaseTimerRef.current = window.setTimeout(() => {
      logoutTransitionReleaseTimerRef.current = null;
      setLogoutTransitionPendingState(false);
      clearLogoutTransitionPending();
    }, LOGOUT_TRANSITION_RELEASE_DELAY_MS);

    return () => {
      if (logoutTransitionReleaseTimerRef.current != null) {
        window.clearTimeout(logoutTransitionReleaseTimerRef.current);
        logoutTransitionReleaseTimerRef.current = null;
      }
    };
  }, [authReady, logoutTransitionPending, publicLocation.path, user?.uid]);

  useEffect(() => {
    return () => {
      if (logoutTransitionReleaseTimerRef.current != null) {
        window.clearTimeout(logoutTransitionReleaseTimerRef.current);
      }
    };
  }, []);

  const clearSignupCheckoutHandoff = useCallback((reason: string = 'manual_clear') => {
    const record = readSignupCheckoutHandoffRecord();
    if (record || signupCheckoutHandoffActive || loginFlowActive) {
      tpStripeFlashLog('signup_checkout_handoff_cleared', {
        reason,
        userUid: user?.uid || null,
        publicPath: readPublicLocation().path,
        hadRuntimeHandoff: signupCheckoutHandoffActive,
        hadLoginFlow: loginFlowActive,
        stage: record?.stage || null,
        startedAt: record?.startedAt || null,
        redirectStartedAt: record?.redirectStartedAt || null,
      });
    }
    setSignupCheckoutHandoffActiveState(false);
    clearSignupCheckoutHandoffActive();
    setAuthIntentPendingState(false);
    setLoginFlowActiveState(false);
    clearLoginFlowActive();
  }, [loginFlowActive, signupCheckoutHandoffActive, user?.uid]);

  const escapeToExistingAccountLogin = useCallback((reason: string = 'existing_account_login') => {
    clearSignupCheckoutHandoff(reason);
    clearBillingNotice();
    beginLogoutTransition();
    void signOut({ scope: 'local' }).catch(() => undefined);
  }, [beginLogoutTransition, clearBillingNotice, clearSignupCheckoutHandoff]);

  const beginSignupCheckoutHandoff = useCallback(() => {
    setSignupCheckoutHandoffActiveState(true);
    setSignupCheckoutHandoffActive();
    tpStripeFlashLog('signup_checkout_handoff_begin', {
      userUid: user?.uid || null,
      publicPath: readPublicLocation().path,
    });
  }, [user?.uid]);

  const markSignupCheckoutRedirectStart = useCallback(() => {
    markSignupCheckoutHandoffRedirectStarted();
    const record = readSignupCheckoutHandoffRecord();
    tpStripeFlashLog('signup_checkout_redirect_committed', {
      userUid: user?.uid || null,
      publicPath: readPublicLocation().path,
      stage: record?.stage || null,
      startedAt: record?.startedAt || null,
      redirectStartedAt: record?.redirectStartedAt || null,
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!recoverSignupCheckoutHandoffOnLoadRef.current) return;

    const record = readSignupCheckoutHandoffRecord();
    tpStripeFlashLog('signup_checkout_handoff_recovered', {
      reason: 'reload_recovery',
      stage: record?.stage || null,
      startedAt: record?.startedAt || null,
      redirectStartedAt: record?.redirectStartedAt || null,
      publicPath: readPublicLocation().path,
    });
    clearSignupCheckoutHandoff('reload_recovery');
    recoverSignupCheckoutHandoffOnLoadRef.current = false;
  }, [clearSignupCheckoutHandoff]);

  // Auto-clear stale signup checkout handoff records that were left behind
  // due to interrupted redirects or failures. This prevents the "Opening your
  // dashboard" surface from persisting across refreshes when the handoff is
  // no longer active. Keep the threshold conservative (2 minutes) to avoid
  // clearing active redirects.
  useEffect(() => {
    try {
      const rec = readSignupCheckoutHandoffRecord();
      if (!rec) return;
      const age = Date.now() - Number(rec.startedAt || 0);
      const redirectAge = rec.redirectStartedAt ? (Date.now() - Number(rec.redirectStartedAt || 0)) : null;
      const STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
      if (age > STALE_THRESHOLD_MS && (!rec.redirectStartedAt || (redirectAge != null && redirectAge > STALE_THRESHOLD_MS))) {
        clearSignupCheckoutHandoff('auto_stale_clear');
      }
    } catch {}
  }, [clearSignupCheckoutHandoff]);

  useEffect(() => {
    const billingReturnKind = readBillingReturnKindFromLocation();
    if (!billingReturnKind) return;
    const record = readSignupCheckoutHandoffRecord();
    if (!record && !signupCheckoutHandoffActive && !loginFlowActive) return;

    tpStripeFlashLog('checkout_return_detected', {
      billingReturnKind,
      userUid: user?.uid || null,
      publicPath: readPublicLocation().path,
      hadRuntimeHandoff: signupCheckoutHandoffActive,
      hadLoginFlow: loginFlowActive,
      stage: record?.stage || null,
      startedAt: record?.startedAt || null,
      redirectStartedAt: record?.redirectStartedAt || null,
    });
    clearSignupCheckoutHandoff(`checkout_return:${billingReturnKind}`);
  }, [clearSignupCheckoutHandoff, loginFlowActive, signupCheckoutHandoffActive, user?.uid]);

  useEffect(() => {
    const handlePopState = () => {
      try {
        const nextPage = resolveAuthenticatedPageFromPath(String(window.location.pathname || '/').trim() || '/');
        if (nextPage) {
          setActive((current) => current === nextPage ? current : nextPage);
          setPublicLocation({ path: null, hash: null });
        }
      } catch {}
      setPublicLocation(readPublicLocation());
    };

    try {
      window.addEventListener('popstate', handlePopState);
    } catch {}

    return () => {
      try { window.removeEventListener('popstate', handlePopState); } catch {}
    };
  }, []);

  useEffect(() => {
    const billingReturnKind = readBillingReturnKindFromLocation();
    if (!billingReturnKind) return;

    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const publicPathname = normalizePublicPath(pathname);
      const nextHash = String(window.location.hash || '').replace(/^#/, '').trim() || null;

      if (publicPathname === ('/login' as PublicRoutePath)) {
        setPublicLocation((current) => (
          current.path === ('/login' as PublicRoutePath) && current.hash === nextHash
            ? current
            : { path: '/login', hash: nextHash }
        ));
        return;
      }

      const nextLocation = buildBillingReturnLoginLocation();
      window.history.replaceState({}, '', nextLocation.href);
      setPublicLocation((current) => (
        current.path === nextLocation.path && current.hash === nextLocation.hash
          ? current
          : { path: nextLocation.path, hash: nextLocation.hash }
      ));
      tpStripeFlashLog('billing_return_route_normalized', {
        billingReturnKind,
        fromPath: pathname,
        toPath: nextLocation.path,
      });
    } catch {}
  }, []);

  useEffect(() => {
    // Apply initial theme to DOM immediately
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Load saved avatar
    const savedAvatar = localStorage.getItem('avatarDataUrl');
    if (savedAvatar) setAvatarUrl(savedAvatar);
  }, []);

  useEffect(() => {
    console.log(`BUILD VERSION: ${import.meta.env.VITE_BUILD_VERSION || 'dev'}`);
  }, []);

  useEffect(() => {
    if (previousAuthReadyRef.current === authReady) return;
    tpStripeFlashLog('auth_loading_changed', {
      authReady,
      previousAuthReady: previousAuthReadyRef.current,
      hasUserSession: !!user?.uid,
      userUid: user?.uid || null,
      loginFlowActive,
      publicPath: publicLocation.path,
    });
    previousAuthReadyRef.current = authReady;
  }, [authReady, loginFlowActive, publicLocation.path, user?.uid]);

  useEffect(() => {
    const currentUserUid = user?.uid ? String(user.uid) : null;
    if (previousUserUidRef.current === currentUserUid) return;
    tpStripeFlashLog('session_observed', {
      userUid: currentUserUid,
      userEmail: user?.email ?? null,
      authReady,
      loginFlowActive,
      publicPath: publicLocation.path,
    });
    previousUserUidRef.current = currentUserUid;
  }, [authReady, loginFlowActive, publicLocation.path, user?.email, user?.uid]);

  useEffect(() => {
    if (previousTenantBootstrapReadyRef.current === tenantBootstrapReady) return;
    tpStripeFlashLog('tenant_bootstrap_state_changed', {
      tenantBootstrapReady,
      previousTenantBootstrapReady: previousTenantBootstrapReadyRef.current,
      tenantBootstrapSlow,
      tenantBootstrapError: tenantBootstrapError || null,
      tenantBootstrapAttempt,
      userUid: user?.uid || null,
      publicPath: publicLocation.path,
    });
    previousTenantBootstrapReadyRef.current = tenantBootstrapReady;
  }, [publicLocation.path, tenantBootstrapAttempt, tenantBootstrapError, tenantBootstrapReady, tenantBootstrapSlow, user?.uid]);

  useEffect(() => {
    const isIgnorableBrowserNoise = (message: string) => {
      const msg = String(message || '');
      return msg.includes('ResizeObserver loop completed with undelivered notifications')
        || msg.includes('ResizeObserver loop limit exceeded')
        || isAbortLikeMessage(msg);
    };

    const shouldCaptureBootFailure = () => {
      try {
        const pathname = String(window.location.pathname || '/').trim() || '/';
        const livePublicPath = normalizePublicPath(pathname);
        if (livePublicPath) return false;
      } catch {}

      const userUid = user?.uid ? String(user.uid) : null;
      const authBootstrapPending = !authReady;
      const tenantBootstrapPending = Boolean(userUid) && !effectiveTenantBootstrapReady && !tenantBootstrapError;
      return authBootstrapPending || tenantBootstrapPending;
    };

    const onError = (event: ErrorEvent) => {
      try {
        const message = event?.message || 'Unknown error';
        if (isIgnorableBrowserNoise(message)) return;
        if (!shouldCaptureBootFailure()) return;
        console.error('[App] fatal workspace startup error', event?.error || message);
        setFatalError(message);
      } catch {}
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = (event && (event as any).reason) || 'Unhandled promise rejection';
        const message = typeof reason === 'string'
          ? reason
          : (reason && typeof reason.message === 'string' ? reason.message : JSON.stringify(reason));
        if (isIgnorableBrowserNoise(message)) return;
        if (!shouldCaptureBootFailure()) return;
        console.error('[App] unhandled rejection during workspace startup', reason);
        setFatalError(message);
      } catch {}
    };
    try {
      window.addEventListener('error', onError);
      window.addEventListener('unhandledrejection', onRejection);
    } catch {}
    return () => {
      try { window.removeEventListener('error', onError); } catch {}
      try { window.removeEventListener('unhandledrejection', onRejection); } catch {}
    };
  }, [authReady, effectiveTenantBootstrapReady, tenantBootstrapError, user?.uid]);

  // Listen for Supabase auth state changes and update UI accordingly
  useEffect(() => {
    // Private stores remain unreadable until the current session and tenant
    // membership have both been verified below.
    invalidatePrivateTradingScope();
    let unsub: (() => void) | null = null;
    let cancelled = false;
    if (bootstrapCorrelationAttemptRef.current !== authBootstrapAttempt) {
      bootstrapCorrelationAttemptRef.current = authBootstrapAttempt;
      bootstrapCorrelationIdRef.current = createBootstrapCorrelationId();
    }
    const bootstrapCorrelationId = bootstrapCorrelationIdRef.current;
    tpStripeFlashLog('session_bootstrap_start', { correlationId: bootstrapCorrelationId });
    setAuthBootstrapError(null);

    const subscribeToSessionChanges = () => {
      if (unsub) return;
      unsub = onSessionChange((nextSession) => {
        if (cancelled) return;
        const nextSessionUserId = String(nextSession?.user?.id ?? '').trim() || null;
        const verifiedScope = getVerifiedPrivateTradingScope();
        if (!nextSessionUserId || (verifiedScope && verifiedScope.userId !== nextSessionUserId)) {
          invalidatePrivateTradingScope();
        }
        const nextUser = nextSession?.user ? sessionToAppIdentity(nextSession) : resolveStoredIdentityFallback();
        authDebugLog('app_session_change', {
          hasSession: !!nextSession?.user,
          userUid: nextSession?.user?.id || null,
          userEmail: nextSession?.user?.email || null,
          publicPath: readPublicLocation().path,
          loginFlowActive: getLoginFlowActive(),
          correlationId: bootstrapCorrelationId,
        });
        tpStripeFlashLog('session_change', {
          hasSession: !!nextSession?.user,
          userUid: nextSession?.user?.id || null,
          userEmail: nextSession?.user?.email || null,
          publicPath: readPublicLocation().path,
          loginFlowActive: getLoginFlowActive(),
          correlationId: bootstrapCorrelationId,
        });
        if (nextSession?.user) {
          authReadyForBootstrapAtRef.current = readNow();
        }
        setAuthIntentPendingState(false);
        setAuthBootstrapError(null);
        setUser(nextUser);
        setAuthReady(true);
        if (nextUser) {
          try {
            const pathname = String(window.location.pathname || '/').trim() || '/';
            const publicPathname = normalizePublicPath(pathname);
            if (publicPathname === '/login' && getLogoutTransitionPending()) {
              setLogoutTransitionPendingState(false);
              clearLogoutTransitionPending();
            }
            const loginFlowStillActive = getLoginFlowActive();
            const signupCheckoutHandoffStillActive = readSignupCheckoutHandoffActive();
            if (!signupCheckoutHandoffStillActive && !loginFlowStillActive && pathname !== '/signup' && (pathname === '/' || pathname === '/login')) {
              setActive('Dashboard');
            }
          } catch {}
        }
      });
    };

    (async () => {
      try {
        const session = await getCurrentSession();
        if (cancelled) return;
        setAuthIntentPendingState(false);
        setAuthBootstrapError(null);
        setUser(session?.user ? sessionToAppIdentity(session) : resolveStoredIdentityFallback());
        authDebugLog('app_session_bootstrap_result', {
          hasSession: !!session?.user,
          userUid: session?.user?.id || null,
          userEmail: session?.user?.email || null,
          authReadyBeforeSet: authReady,
          loginFlowActive,
          publicPath: readPublicLocation().path,
          correlationId: bootstrapCorrelationId,
        });
        tpStripeFlashLog('session_bootstrap_result', {
          hasSession: !!session?.user,
          userUid: session?.user?.id || null,
          userEmail: session?.user?.email || null,
          authReadyBeforeSet: authReady,
          loginFlowActive,
          publicPath: readPublicLocation().path,
          correlationId: bootstrapCorrelationId,
        });
        if (session?.user) {
          authReadyForBootstrapAtRef.current = readNow();
        }
        setAuthReady(true);
        subscribeToSessionChanges();
      } catch (err) {
        if (cancelled) return;
        console.error('[App] auth listener error', err);
        const internalMessage = getErrorMessage(err) || 'Auth session bootstrap failed.';
        const message = 'Unable to verify your session. Please try again.';
        authDebugLog('app_session_bootstrap_error', {
          error: internalMessage,
          publicPath: readPublicLocation().path,
          loginFlowActive,
          correlationId: bootstrapCorrelationId,
        });
        tpStripeFlashLog('session_bootstrap_error', {
          error: internalMessage,
          publicPath: readPublicLocation().path,
          loginFlowActive,
          correlationId: bootstrapCorrelationId,
        });
        setAuthIntentPendingState(false);
        setUser(null);
        setAuthReady(false);
        setAuthBootstrapError(message);
        try { subscribeToSessionChanges(); } catch (subscriptionError) {
          console.error('[App] auth listener subscription error after bootstrap error', subscriptionError);
        }
      }
    })();

    return () => { cancelled = true; try { if (unsub) unsub(); } catch {} };
  }, [authBootstrapAttempt]);

  // Resolve the active white-label branding from the authenticated tenant/workspace.
  // Server-authoritative: falls back to standard Trade-Proof branding when absent.
  // Pre-login public branding (publicBrandingAppliedRef) is preserved while no
  // user session exists so tenant branding appears before authentication.
  useEffect(() => {
    let cancelled = false;
    const userId = user?.uid;
    if (!userId || !authReady) {
      if (!userId && !publicBrandingAppliedRef.current) {
        setWhiteLabelAppBranding(null);
        const resetHostname = String(typeof window !== 'undefined' ? window.location.hostname || '' : '').trim().toLowerCase();
        if (classifyTenantHostname(resetHostname) !== 'base') {
          // Tenant hostnames stay neutral while tenant branding resolves — never
          // flash the default Trade-Proof title/favicon on a tenant host.
          applyNeutralWhiteLabelBrandingToDocument();
        } else {
          applyWhiteLabelBrandingToDocument(null);
        }
      }
      return;
    }

    (async () => {
      try {
        const branding = await fetchWhiteLabelBranding();
        if (cancelled) return;
        // Install the tenant asset entitlement policy from the server-provided
        // white-label launch controls so every trade boundary enforces the same
        // enabled asset classes as the public branding path.
        setActiveTenantSlugForDisplayScope(branding?.tenantSlug || null);
        setTenantDisplayAssetClasses(
          branding?.tenantSlug || null,
          getTenantDisplayAssetClassesFromLaunchControls(branding?.launchControls),
        );
        const appBranding = branding ? buildWhiteLabelAppBranding(branding) : null;
        publicBrandingAppliedRef.current = Boolean(appBranding);
        setWhiteLabelAppBranding(appBranding);
        setCachedWhiteLabelAppBranding(appBranding);
        applyWhiteLabelBrandingToDocument(appBranding);
      } catch {
        if (cancelled) return;
        publicBrandingAppliedRef.current = false;
        setWhiteLabelAppBranding(null);
        const resetHostname = String(typeof window !== 'undefined' ? window.location.hostname || '' : '').trim().toLowerCase();
        if (classifyTenantHostname(resetHostname) !== 'base') {
          applyNeutralWhiteLabelBrandingToDocument();
        } else {
          applyWhiteLabelBrandingToDocument(null);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [authReady, user?.uid]);

  // Public (pre-login) tenant branding. Runs once on initial page load for
  // tenant hostnames only. Never blocks the base Trade-Proof login page.
  // After authentication the protected tenant bootstrap + authenticated branding
  // flow (same authoritative resolver) takes over and confirms the tenant match.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hostname = String(window.location.hostname || '').trim().toLowerCase();
    // Tenant hostnames (wildcard *.tradeproof.app / *.localhost / *.local AND
    // verified custom domains) request public branding before auth. Base hosts
    // (localhost, tradeproof.app, app.trade-proof.com, preview deployments) keep
    // the normal Trade-Proof behavior and never block on tenant branding.
    if (!hostname || isBaseTradeProofHostname(hostname)) return;
    let cancelled = false;
    (async () => {
      const result = await fetchPublicTenantBranding();
      if (cancelled) return;
      if (!result || result.state === 'not_found') {
        setPublicTenantState(result?.state || 'not_found');
        return;
      }
      setPublicTenantState(result.state);
      setPublicRegistrationEnabled(result.publicRegistrationEnabled);
      if (result.branding) {
        // Install the tenant asset entitlement policy from the same server
        // payload that drives pre-login branding so canonical trade reads and
        // branding agree on the tenant's enabled asset classes.
        setActiveTenantSlugForDisplayScope(result.tenantSlug || null);
        setTenantDisplayAssetClasses(
          result.tenantSlug || null,
          getTenantDisplayAssetClassesFromLaunchControls(result.branding.launchControls),
        );
        const appBranding = buildWhiteLabelAppBranding(result.branding);
        publicBrandingAppliedRef.current = true;
        setWhiteLabelAppBranding(appBranding);
        setCachedWhiteLabelAppBranding(appBranding);
        applyWhiteLabelBrandingToDocument(appBranding);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicRegistrationState().then((enabled) => {
      if (!cancelled && enabled != null) setPublicRegistrationEnabled(enabled);
    });
    return () => { cancelled = true; };
  }, []);

  // Load demo preferences on login
  useEffect(() => {
    if (!user?.uid) {
      setDemoModePermanentlyDisabled(false);
      return;
    }
    let cancelled = false;
    async function loadPreference() {
      const isPermanentlyDisabled = await fetchDemoModePreference();
      if (cancelled) return;
      if (isPermanentlyDisabled) {
        setDemoModePermanentlyDisabled(true);
        disableDemoMode();
      } else {
        setDemoModePermanentlyDisabled(false);
      }
    }
    loadPreference();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Listen for the custom request-exit-demo event
  useEffect(() => {
    const handleRequestExit = () => {
      setIsExitDemoModalOpen(true);
    };
    window.addEventListener('tradeproof:request-exit-demo', handleRequestExit);
    return () => {
      window.removeEventListener('tradeproof:request-exit-demo', handleRequestExit);
    };
  }, []);

  // Sync React state for temporary disable when demo mode changes
  useEffect(() => {
    const handleDemoModeChange = () => {
      console.log('[App] handleDemoModeChange called, isDemoModeTemporarilyDisabled =', isDemoModeTemporarilyDisabled());
      setDemoModeTemporarilyDisabledState(isDemoModeTemporarilyDisabled());
    };
    window.addEventListener('tradeproof:dashboard-demo-mode-changed', handleDemoModeChange);
    return () => {
      window.removeEventListener('tradeproof:dashboard-demo-mode-changed', handleDemoModeChange);
    };
  }, []);

  const handleConfirmExitPermanent = useCallback(async () => {
    setIsExitDemoModalOpen(false);
    setDemoModePermanentlyDisabled(true);
    await saveDemoModePreference(true);
    disableDemoMode();
  }, []);

  const handleConfirmExitTemporary = useCallback(() => {
    setIsExitDemoModalOpen(false);
    setDemoModeTemporarilyDisabledState(true);
    setDemoModeTemporarilyDisabled(true);
  }, []);

  useEffect(() => {
    if (!authReady) return;

    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const publicPathname = normalizePublicPath(pathname);
      if (publicPathname !== ('/login' as PublicRoutePath) && publicPathname !== ('/signup' as PublicRoutePath)) return;

      const searchParams = new URLSearchParams(window.location.search || '');
      const resetSuccessRequested = searchParams.get('reset') === 'success';

      if (!loginSuccessMessage && resetSuccessRequested) {
        setLoginSuccessMessage({
          message: LOGIN_RESET_SUCCESS_MESSAGE,
          nonce: Date.now(),
        });
        try {
          window.history.replaceState({}, '', pathname);
          setPublicLocation({ path: publicPathname, hash: null });
        } catch {}
        return;
      }

      if (publicPathname === ('/signup' as PublicRoutePath)) {
        if (!recoverSignupCheckoutHandoffOnLoadRef.current) {
          const record = readSignupCheckoutHandoffRecord();
          if (record) {
            clearSignupCheckoutHandoff('signup_route_cleanup');
          }
        }
        clearLoginAuthErrorNotice();
        clearLogoutTransitionNotice();
        setLoginFlowActiveState(false);
        clearLoginFlowActive();
        return;
      }

      if (loginSuccessMessage || loginAuthError) return;

      const storedSuccess = getStoredLoginSuccessMessage();
      if (storedSuccess) {
        setLoginSuccessMessage(storedSuccess);
        clearStoredLoginSuccessMessage();
      }

      const storedError = getStoredLoginAuthError();
      if (storedError) {
        setLoginAuthError(storedError);
      }
    } catch {}
  }, [authReady, clearLoginAuthErrorNotice, clearLogoutTransitionNotice, loginAuthError, loginSuccessMessage, publicLocation.path]);

  useEffect(() => {
    if (!user?.uid) {
      invalidatePrivateTradingScope();
      tenantBootstrapKeyRef.current = null;
      tenantBootstrapSlugRef.current = null;
      clearAuthoritativeTenantSlug();
      setTenantBootstrapReady(false);
      setTenantBootstrapSlow(false);
      setTenantMembershipRole(null);
      setTenantBootstrapError(null);
      return;
    }

    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const publicPathname = normalizePublicPath(pathname);
      if (publicPathname === ('/signup' as PublicRoutePath)) {
        return;
      }
      if (publicPathname === '/auth/callback' || publicPathname === '/forgot-password' || publicPathname === '/auth/reset-password') {
        return;
      }
      if (/^\/(legal|terms|terms-of-service|privacy|privacy-policy)\/?$/i.test(pathname)) {
        tenantBootstrapKeyRef.current = null;
        setTenantBootstrapReady(false);
        setTenantBootstrapSlow(false);
        setTenantMembershipRole(null);
        setTenantBootstrapError(null);
        return;
      }
    } catch {}

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const nextKey = `${String(user.uid)}:${hostname}:${tenantBootstrapAttempt}`;
    if (tenantBootstrapKeyRef.current === nextKey) return;

    // A new tenant bootstrap invalidates prior private snapshots immediately;
    // no user-scoped read is allowed while the new scope is unresolved.
    invalidatePrivateTradingScope();

    tenantBootstrapKeyRef.current = nextKey;
    const bootstrapCorrelationId = bootstrapCorrelationIdRef.current;
    let cancelled = false;
    let bootstrapSettled = false;
    const controller = new AbortController();

    setTenantBootstrapReady(false);
    setTenantBootstrapSlow(false);
    setTenantMembershipRole(null);
    setTenantBootstrapError(null);
    authDebugLog('tenant_bootstrap_start', {
      userUid: user.uid,
      tenantBootstrapAttempt,
      publicPath: publicLocation.path,
      hostname,
      tenantKey: nextKey,
      correlationId: bootstrapCorrelationId,
    });
    tpStripeFlashLog('tenant_bootstrap_start', {
      userUid: user.uid,
      tenantBootstrapAttempt,
      publicPath: publicLocation.path,
      hostname,
      tenantKey: nextKey,
      correlationId: bootstrapCorrelationId,
    });

    // A slow threshold updates messaging only. It never grants access or fails the request.
    try {
      if (tenantBootstrapTimeoutRef.current != null) {
        window.clearTimeout(tenantBootstrapTimeoutRef.current);
        tenantBootstrapTimeoutRef.current = null;
      }
      tenantBootstrapTimeoutRef.current = window.setTimeout(() => {
        try {
          setTenantBootstrapSlow(true);
          console.warn('[TP] tenant bootstrap is taking longer than usual', { userUid: user.uid, publicPath: publicLocation.path });
          tpStripeFlashLog('tenant_bootstrap_slow', { userUid: user.uid, publicPath: publicLocation.path });
        } catch {}
      }, WORKSPACE_LOAD_TIMEOUT_MS) as unknown as number;
    } catch {}

    (async () => {
      try {
        bootstrapStartedAtRef.current = readNow();
        const bootstrapResult = await bootstrapTenantMembership({ hostname, signal: controller.signal, correlationId: bootstrapCorrelationId || undefined });
        if (cancelled) return;
        try {
          if (tenantBootstrapTimeoutRef.current != null) {
            window.clearTimeout(tenantBootstrapTimeoutRef.current);
            tenantBootstrapTimeoutRef.current = null;
          }
        } catch {}

        tpStripeFlashLog('tenant_bootstrap_finish', {
          userUid: user.uid,
          tenantBootstrapAttempt,
          publicPath: publicLocation.path,
          tenantBootstrapReady: true,
          correlationId: bootstrapCorrelationId,
        });
        bootstrapSettled = true;
        authDebugLog('tenant_bootstrap_finish', {
          userUid: user.uid,
          tenantBootstrapAttempt,
          publicPath: publicLocation.path,
          tenantBootstrapReady: true,
          correlationId: bootstrapCorrelationId,
        });
        clearStoredLoginAuthError();
        setLoginAuthError(null);
        setTenantBootstrapReady(true);
        setTenantBootstrapSlow(false);
        setTenantMembershipRole(bootstrapResult.role || null);
        setTenantBootstrapError(null);
        tenantBootstrapSlugRef.current = String(bootstrapResult.tenantSlug || '').trim() || null;
        setAuthoritativeTenantSlug(tenantBootstrapSlugRef.current);
        const verifiedSession = await getCurrentSession();
        if (cancelled || String(verifiedSession?.user?.id ?? '').trim() !== String(user.uid)) return;
        activatePrivateTradingScope({ tenantId: bootstrapResult.tenantId, userId: user.uid });
      } catch (err: any) {
        if (cancelled) return;
        invalidatePrivateTradingScope();
        if (isAbortLikeError(err) || String(err?.code || '').trim().toLowerCase() === 'tenant_bootstrap_aborted') {
          try {
            if (tenantBootstrapTimeoutRef.current != null) {
              window.clearTimeout(tenantBootstrapTimeoutRef.current);
              tenantBootstrapTimeoutRef.current = null;
            }
          } catch {}
          tenantBootstrapKeyRef.current = null;
          const message = getErrorMessage(err) || 'Tenant bootstrap request was interrupted. Please retry.';
          setTenantBootstrapError(message);
          setTenantBootstrapReady(false);
          setTenantBootstrapSlow(false);
          setTenantMembershipRole(null);
          authDebugLog('tenant_bootstrap_aborted', {
            userUid: user.uid,
            tenantBootstrapAttempt,
            publicPath: publicLocation.path,
            error: getErrorMessage(err),
            code: err?.code || null,
            status: err?.status ?? null,
          });
          tpStripeFlashLog('tenant_bootstrap_aborted', {
            userUid: user.uid,
            tenantBootstrapAttempt,
            publicPath: publicLocation.path,
            error: getErrorMessage(err),
            code: err?.code || null,
            status: err?.status ?? null,
          });
          bootstrapSettled = true;
          return;
        }
        console.error('[App] tenant bootstrap error', err);
        authDebugLog('tenant_bootstrap_error', {
          userUid: user.uid,
          tenantBootstrapAttempt,
          publicPath: publicLocation.path,
          error: getErrorMessage(err),
          code: err?.code || null,
          status: err?.status ?? null,
        });
        tpStripeFlashLog('tenant_bootstrap_error', {
          userUid: user.uid,
          tenantBootstrapAttempt,
          publicPath: publicLocation.path,
          error: getErrorMessage(err),
          code: err?.code || null,
          status: err?.status ?? null,
        });
        try {
          if (tenantBootstrapTimeoutRef.current != null) {
            window.clearTimeout(tenantBootstrapTimeoutRef.current);
            tenantBootstrapTimeoutRef.current = null;
          }
        } catch {}
        tenantBootstrapKeyRef.current = null;
        bootstrapSettled = true;
        const code = String(err?.code || '').trim().toLowerCase();
        const status = Number(err?.status || 0);
        const message = getErrorMessage(err) || 'Unable to prepare tenant access for this hostname.';
        const normalizedMessage = message.toLowerCase();
        const isTenantAccessDenied =
          code === 'tenant_access_denied' ||
          normalizedMessage.includes('not authorized for') ||
          normalizedMessage.includes('not assigned to the requested tenant') ||
          normalizedMessage.includes('not active for the requested tenant');
        const isAuthSessionExpired =
          code === 'auth_session_expired' ||
          code === 'auth_session_invalid' ||
          code === 'auth_session_missing' ||
          status === 401 ||
          normalizedMessage.includes('token is expired') ||
          normalizedMessage.includes('invalid jwt') ||
          normalizedMessage.includes('session expired');

        if (isAuthSessionExpired) {
          authDebugLog('app_tenant_bootstrap_redirect_login', {
            reason: 'auth_session_expired',
            userUid: user.uid,
            publicPath: publicLocation.path,
            hostname,
            code: err?.code || null,
            status: err?.status ?? null,
            message,
          });
          clearLoginSuccessNotice();
          const storedError = setStoredLoginAuthError(SESSION_EXPIRED_LOGIN_MESSAGE);
          setLoginAuthError(storedError || {
            message: SESSION_EXPIRED_LOGIN_MESSAGE,
            nonce: Date.now(),
          });
          setTenantBootstrapError(null);
          setTenantBootstrapReady(false);
          setTenantMembershipRole(null);
          clearSignupCheckoutHandoff();
          try {
            window.history.replaceState({}, '', '/login');
            setPublicLocation({ path: '/login', hash: null });
          } catch {}
          setUser(null);
          void signOut({ scope: 'local' }).catch(() => undefined);
          return;
        }

        if (isTenantAccessDenied) {
          deniedResponseAtRef.current = readNow();
          deniedTimingsRef.current = err?.timings || null;
          authDebugLog('app_tenant_bootstrap_redirect_login', {
            reason: 'tenant_access_denied',
            userUid: user.uid,
            publicPath: publicLocation.path,
            hostname,
            code: err?.code || null,
            status: err?.status ?? null,
            message,
          });
          tpStripeFlashLog('tenant_bootstrap_denied', {
            authToBootstrapStartMs: roundDurationMs(authReadyForBootstrapAtRef.current == null || bootstrapStartedAtRef.current == null ? null : bootstrapStartedAtRef.current - authReadyForBootstrapAtRef.current),
            sessionLookupMs: err?.timings?.sessionLookupMs ?? null,
            bootstrapDurationMs: err?.timings?.bootstrapDurationMs ?? null,
            totalTo403Ms: roundDurationMs(authReadyForBootstrapAtRef.current == null || deniedResponseAtRef.current == null ? null : deniedResponseAtRef.current - authReadyForBootstrapAtRef.current),
          });
          clearLoginSuccessNotice();
          setDeniedLoginAuthError(message);
          setTenantBootstrapError(null);
          setTenantBootstrapReady(false);
          setTenantMembershipRole(null);
          clearSignupCheckoutHandoff();
          try {
            window.history.replaceState({}, '', '/login');
            setPublicLocation({ path: '/login', hash: null });
          } catch {}
          setUser(null);
          void signOut({ scope: 'local' }).catch(() => undefined);
          return;
        }
        clearStoredLoginAuthError();
        setTenantMembershipRole(null);
        setTenantBootstrapError(message);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      try {
        if (tenantBootstrapTimeoutRef.current != null) {
          window.clearTimeout(tenantBootstrapTimeoutRef.current);
          tenantBootstrapTimeoutRef.current = null;
        }
      } catch {}
      if (!bootstrapSettled) {
        tenantBootstrapKeyRef.current = null;
      }
    };
  }, [clearSignupCheckoutHandoff, publicLocation.path, tenantBootstrapAttempt, user?.uid]);

  useEffect(() => {
    let cancelled = false;
    async function loadMode() {
      try {
        const status = await fetchReplayLaunchControlsStatus();
        if (!cancelled && status) {
          setNavigationLayoutMode(status.navigationLayoutMode || 'both');
          setTenantThemeMode(status.themeMode || 'light_dark');
        }
      } catch (e) {
        console.error('[App] Failed to fetch launch controls status', e);
      }
    }
    loadMode();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, tenantBootstrapReady]);

  useEffect(() => {
    if (tenantThemeMode === 'light_only') setTheme('light');
    else if (tenantThemeMode === 'dark_only') setTheme('dark');
  }, [tenantThemeMode]);

  const tenantThemeToggle = tenantThemeMode === 'light_dark' ? setTheme : undefined;

  useEffect(() => {
    if (navigationLayoutMode === 'side_only' && navLayout !== 'sidebar') {
      setNavLayout('sidebar');
    } else if (navigationLayoutMode === 'top_only' && navLayout !== 'top') {
      setNavLayout('top');
    }
  }, [navigationLayoutMode, navLayout]);

  useEffect(() => {
    try {
      localStorage.setItem('tradeproof:navigation-layout', navLayout);
    } catch {}
  }, [navLayout]);

  const refreshBillingState = useCallback(async () => {
    const requestId = ++billingAccessRequestIdRef.current;

    if (BILLING_BYPASS_ACTIVE) {
      setBillingState(BILLING_BYPASS_STATE);
      setBillingStateOwnerUid(BILLING_BYPASS_OWNER_UID);
      setBillingReady(true);
      setBillingError(null);
      return;
    }

    const canConfirmByCheckoutSession = checkoutReturn.visible && hasCheckoutReturnSessionId();

    if (!user?.uid && !canConfirmByCheckoutSession) {
      setBillingState(null);
      setBillingStateOwnerUid(null);
      setBillingReady(false);
      setBillingError(null);
      return;
    }

    setBillingReady(false);
    setBillingError(null);
    authDebugLog('billing_status_fetch_start', {
      kind: 'status_v2',
      userUid: user.uid,
      email: user?.email ? String(user.email) : null,
      publicPath: publicLocation.path,
      checkoutReturnVisible: checkoutReturn.visible,
      correlationId: bootstrapCorrelationIdRef.current,
    });
    tpStripeFlashLog('billing_status_fetch_start', {
      kind: 'status_v2',
      userUid: user.uid,
      email: user?.email ? String(user.email) : null,
      publicPath: publicLocation.path,
      checkoutReturnVisible: checkoutReturn.visible,
      correlationId: bootstrapCorrelationIdRef.current,
    });
    try {
      const next = await fetchBillingStatus(
        {
          uid: String(user.uid),
          email: user?.email ? String(user.email) : null,
        },
        { tenantSlug: tenantBootstrapSlugRef.current, correlationId: bootstrapCorrelationIdRef.current || undefined },
      );
      if (requestId !== billingAccessRequestIdRef.current) return;
      tpStripeFlashLog('billing_status_fetch_finish', {
        kind: 'status_v2',
        userUid: user.uid,
        billingReady: true,
        subscriptionStatus: next.subscriptionStatus,
        hasAccess: next.hasAccess,
        checkoutEligible: next.checkoutEligible,
        customerPortalEligible: next.customerPortalEligible,
        configured: next.configured,
        hasAccessDecision: next.hasAccess === true,
        correlationId: bootstrapCorrelationIdRef.current,
      });
      authDebugLog('billing_status_fetch_finish', {
        kind: 'status_v2',
        userUid: user.uid,
        billingReady: true,
        subscriptionStatus: next.subscriptionStatus,
        hasAccess: next.hasAccess,
        checkoutEligible: next.checkoutEligible,
        customerPortalEligible: next.customerPortalEligible,
        configured: next.configured,
        correlationId: bootstrapCorrelationIdRef.current,
      });
      setBillingStateOwnerUid(String(user.uid));
      setBillingState(next);
    } catch (err: any) {
      if (requestId !== billingAccessRequestIdRef.current) return;
      if (isAbortLikeError(err)) {
        authDebugLog('billing_status_fetch_aborted', {
          kind: 'status_v2',
          userUid: user.uid,
          error: getErrorMessage(err),
        });
        tpStripeFlashLog('billing_status_fetch_aborted', {
          kind: 'status_v2',
          userUid: user.uid,
          error: getErrorMessage(err),
        });
        setBillingError(getErrorMessage(err) || 'Billing access verification was interrupted. Please retry.');
        return;
      }
      authDebugLog('billing_status_fetch_error', {
        kind: 'status_v2',
        userUid: user.uid,
        error: getErrorMessage(err) || 'Unable to verify billing access.',
      });
      tpStripeFlashLog('billing_status_fetch_error', {
        kind: 'status_v2',
        userUid: user.uid,
        error: getErrorMessage(err) || 'Unable to verify billing access.',
      });
      setBillingError(getErrorMessage(err) || 'Unable to verify billing access.');
    } finally {
      if (requestId === billingAccessRequestIdRef.current) {
        setBillingReady(true);
      }
    }
  }, [checkoutReturn.visible, publicLocation.path, user]);

  const refreshCheckoutReturnBillingState = useCallback(async () => {
    if (BILLING_BYPASS_ACTIVE) {
      setBillingState(BILLING_BYPASS_STATE);
      setBillingStateOwnerUid(BILLING_BYPASS_OWNER_UID);
      setBillingReady(true);
      setBillingError(null);
      return BILLING_BYPASS_STATE;
    }

    if (!user?.uid) {
      setBillingState(null);
      setBillingStateOwnerUid(null);
      setBillingReady(false);
      setBillingError(null);
      return null;
    }

    setBillingReady(false);
    setBillingError(null);
    authDebugLog('billing_status_fetch_start', {
      kind: 'checkout_return',
      userUid: user?.uid || null,
      email: user?.email ? String(user.email) : null,
      publicPath: publicLocation.path,
      checkoutReturnVisible: checkoutReturn.visible,
    });
    tpStripeFlashLog('billing_status_fetch_start', {
      kind: 'checkout_return',
      userUid: user?.uid || null,
      email: user?.email ? String(user.email) : null,
      publicPath: publicLocation.path,
      checkoutReturnVisible: checkoutReturn.visible,
    });
    try {
      const next = await fetchCheckoutReturnBillingStatus({
        uid: user?.uid ? String(user.uid) : '',
        email: user?.email ? String(user.email) : null,
      });
      tpStripeFlashLog('billing_status_fetch_finish', {
        kind: 'checkout_return',
        userUid: user?.uid || null,
        billingReady: true,
        subscriptionStatus: next.subscriptionStatus,
        hasAccess: next.hasAccess,
        checkoutEligible: next.checkoutEligible,
        customerPortalEligible: next.customerPortalEligible,
        configured: next.configured,
        hasAccessDecision: next.hasAccess === true,
      });
      authDebugLog('billing_status_fetch_finish', {
        kind: 'checkout_return',
        userUid: user?.uid || null,
        billingReady: true,
        subscriptionStatus: next.subscriptionStatus,
        hasAccess: next.hasAccess,
        checkoutEligible: next.checkoutEligible,
        customerPortalEligible: next.customerPortalEligible,
        configured: next.configured,
      });
      setBillingStateOwnerUid(user?.uid ? String(user.uid) : 'checkout-session');
      setBillingState(next);
      return next;
    } catch (err: any) {
      if (isAbortLikeError(err)) {
        authDebugLog('billing_status_fetch_aborted', {
          kind: 'checkout_return',
          userUid: user?.uid || null,
          error: getErrorMessage(err),
        });
        tpStripeFlashLog('billing_status_fetch_aborted', {
          kind: 'checkout_return',
          userUid: user?.uid || null,
          error: getErrorMessage(err),
        });
        return null;
      }
      authDebugLog('billing_status_fetch_error', {
        kind: 'checkout_return',
        userUid: user?.uid || null,
        error: getErrorMessage(err) || 'Unable to verify billing access.',
      });
      tpStripeFlashLog('billing_status_fetch_error', {
        kind: 'checkout_return',
        userUid: user?.uid || null,
        error: getErrorMessage(err) || 'Unable to verify billing access.',
      });
      setBillingState(null);
      setBillingError(getErrorMessage(err) || 'Unable to verify billing access.');
      return null;
    } finally {
      setBillingReady(true);
    }
  }, [checkoutReturn.visible, publicLocation.path, user]);

  useEffect(() => {
    if (BILLING_BYPASS_ACTIVE) {
      if (billingStateOwnerUid !== BILLING_BYPASS_OWNER_UID) {
        setBillingStateOwnerUid(BILLING_BYPASS_OWNER_UID);
      }
      return;
    }

    const currentUserUid = user?.uid ? String(user.uid) : null;
    if (!currentUserUid) {
      if (checkoutReturn.visible && hasCheckoutReturnSessionId()) {
        return;
      }
      setBillingState(null);
      setBillingStateOwnerUid(null);
      setBillingReady(false);
      setBillingError(null);
      return;
    }

    if (billingStateOwnerUid && billingStateOwnerUid !== currentUserUid) {
      setBillingState(null);
      setBillingStateOwnerUid(null);
      setBillingReady(false);
      setBillingError(null);
    }
  }, [billingStateOwnerUid, checkoutReturn.visible, user?.uid]);

  const replaceCheckoutReturnLocation = useCallback((target?: { pathname: string; publicPath: PublicRoutePath | null; hash?: string | null }) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('billing');
      url.searchParams.delete('plan');
      if (target?.pathname) {
        url.pathname = target.pathname;
        url.hash = target.hash ? `#${target.hash}` : '';
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {}

    if (target) {
      setPublicLocation({ path: target.publicPath, hash: target.hash || null });
    }
  }, []);

  const dismissCheckoutReturn = useCallback((target?: { pathname: string; publicPath: PublicRoutePath | null; hash?: string | null }) => {
    clearSignupCheckoutHandoff('dismiss_checkout_return');
    replaceCheckoutReturnLocation(target);
    setCheckoutReturn((current) => ({
      ...current,
      visible: false,
      status: 'idle',
      attempts: 0,
      failureReason: null,
    }));
  }, [clearSignupCheckoutHandoff, replaceCheckoutReturnLocation]);

  const retryCheckoutVerification = useCallback(() => {
    setCheckoutReturnSessionRecovery({ attempts: 0, failed: false });
    setCheckoutReturn((current) => ({
      ...current,
      visible: true,
      status: 'pending',
      attempts: 0,
      failureReason: null,
    }));
    void refreshCheckoutReturnBillingState();
  }, [refreshCheckoutReturnBillingState, user?.uid]);

  const exitCheckoutReturnFailure = useCallback(() => {
    if (user?.uid && (BILLING_BYPASS_ACTIVE || hasConfirmedBillingAccess(billingState))) {
      dismissCheckoutReturn({ pathname: '/dashboard', publicPath: null, hash: null });
      try { setActive('Dashboard'); } catch {}
      return;
    }

    dismissCheckoutReturn({ pathname: '/login', publicPath: '/login', hash: null });
  }, [billingState, dismissCheckoutReturn, user?.uid]);

  const continueCheckoutReturnToWorkspace = useCallback((source: 'cta' | 'timer') => {
    if (checkoutReturnNavigationCommittedRef.current) return;
    if (!(BILLING_BYPASS_ACTIVE || hasConfirmedBillingAccess(billingState))) {
      dismissCheckoutReturn({ pathname: '/login', publicPath: '/login', hash: null });
      return;
    }
    checkoutReturnNavigationCommittedRef.current = true;
    clearSignupCheckoutHandoff(`checkout_return_continue:${source}`);
    setFirstSessionOnboardingRequested(true);
    try {
      sessionStorage.setItem(FIRST_SESSION_ONBOARDING_REQUESTED_KEY, 'true');
    } catch {}
    dismissCheckoutReturn({ pathname: '/dashboard', publicPath: null, hash: null });
    try { setActive('Dashboard'); } catch {}
  }, [billingState, clearSignupCheckoutHandoff, dismissCheckoutReturn]);

  const completeFirstSessionOnboarding = useCallback(() => {
    setFirstSessionOnboardingRequested(false);
    try {
      localStorage.setItem(firstSessionOnboardingCompletedKey, 'true');
    } catch {}
    try {
      sessionStorage.removeItem(FIRST_SESSION_ONBOARDING_REQUESTED_KEY);
    } catch {}
  }, [firstSessionOnboardingCompletedKey]);

  const handleFirstSessionDemoTrade = useCallback(() => {
    completeFirstSessionOnboarding();
    enableDemoMode();
  }, [completeFirstSessionOnboarding]);

  useEffect(() => {
    if (checkoutReturn.visible) return;
    if (!user?.uid || !effectiveTenantBootstrapReady) return;
    void refreshBillingState();
  }, [checkoutReturn.visible, refreshBillingState, effectiveTenantBootstrapReady, user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible || checkoutReturn.status !== 'pending') return;
    if ((!user?.uid && !hasCheckoutReturnSessionId()) || !effectiveTenantBootstrapReady) return;
    void refreshCheckoutReturnBillingState();
  }, [checkoutReturn.status, checkoutReturn.visible, effectiveTenantBootstrapReady, refreshCheckoutReturnBillingState, user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible) {
      setCheckoutReturnSessionRecovery((current) => (
        current.attempts === 0 && !current.failed
          ? current
          : { attempts: 0, failed: false }
      ));
      return undefined;
    }

    if (user?.uid) {
      setCheckoutReturn((current) => (
        current.visible && current.status === 'failed' && current.failureReason === 'session'
          ? { ...current, status: 'pending', attempts: 0, failureReason: null }
          : current
      ));
      setCheckoutReturnSessionRecovery((current) => (
        current.attempts === 0 && !current.failed
          ? current
          : { attempts: 0, failed: false }
      ));
      return undefined;
    }

    if (!authReady || checkoutReturnSessionRecovery.failed) {
      return undefined;
    }

    if (checkoutReturnSessionRecovery.attempts >= CHECKOUT_RETURN_SESSION_RECOVERY_MAX_ATTEMPTS) {
      tpStripeFlashLog('checkout_return_session_recovery_exhausted', {
        attempts: checkoutReturnSessionRecovery.attempts,
        publicPath: publicLocation.path,
      });
      setCheckoutReturnSessionRecovery((current) => current.failed ? current : { ...current, failed: true });
      setCheckoutReturn((current) => (
        current.visible && current.status === 'pending'
          ? { ...current, status: 'failed', failureReason: 'session' }
          : current
      ));
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        const attempt = checkoutReturnSessionRecovery.attempts + 1;
        try {
          tpStripeFlashLog('checkout_return_session_recovery_start', {
            attempt,
            publicPath: publicLocation.path,
          });
          const session = await getCurrentSession();
          const nextUser = sessionToAppIdentity(session);
          if (nextUser?.uid) {
            tpStripeFlashLog('checkout_return_session_recovery_success', {
              attempt,
              userUid: nextUser.uid,
              publicPath: publicLocation.path,
            });
            setUser(nextUser);
            setAuthReady(true);
            setCheckoutReturnSessionRecovery({ attempts: 0, failed: false });
            return;
          }

          tpStripeFlashLog('checkout_return_session_recovery_retry', {
            attempt,
            publicPath: publicLocation.path,
          });
        } catch (err) {
          tpStripeFlashLog('checkout_return_session_recovery_error', {
            attempt,
            error: String((err as Error)?.message || err),
            publicPath: publicLocation.path,
          });
        }

        setCheckoutReturnSessionRecovery((current) => current.failed ? current : {
          ...current,
          attempts: current.attempts + 1,
        });
      })();
    }, CHECKOUT_RETURN_SESSION_RECOVERY_RETRY_MS);

    return () => window.clearTimeout(timer);
  }, [authReady, checkoutReturn.failureReason, checkoutReturn.status, checkoutReturn.visible, checkoutReturnSessionRecovery.attempts, checkoutReturnSessionRecovery.failed, publicLocation.path, user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible || !user?.uid || !effectiveTenantBootstrapReady) return;
    if (checkoutReturn.status !== 'confirmed') return;
    if (checkoutReturnNavigationCommittedRef.current) return;
    continueCheckoutReturnToWorkspace('timer');
  }, [checkoutReturn.status, checkoutReturn.visible, continueCheckoutReturnToWorkspace, effectiveTenantBootstrapReady, user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible || checkoutReturn.status !== 'pending') return undefined;
    if ((!user?.uid && !hasCheckoutReturnSessionId()) || !effectiveTenantBootstrapReady) return undefined;

    if (!billingReady) return undefined;

    if (hasConfirmedBillingAccess(billingState)) {
      setCheckoutReturn((current) => current.visible ? { ...current, status: 'confirmed', failureReason: null } : current);
      return undefined;
    }

    if (checkoutReturn.attempts < CHECKOUT_RETURN_BILLING_MAX_ATTEMPTS) {
      const timer = window.setTimeout(() => {
        setCheckoutReturn((current) => (
          current.visible && current.status === 'pending'
            ? { ...current, attempts: current.attempts + 1 }
            : current
        ));
        void refreshCheckoutReturnBillingState();
      }, CHECKOUT_RETURN_BILLING_RETRY_MS);

      return () => window.clearTimeout(timer);
    }

    setCheckoutReturn((current) => (
      current.visible && current.status === 'pending'
        ? { ...current, status: 'failed', failureReason: 'billing' }
        : current
    ));

    return undefined;
  }, [billingReady, billingState, checkoutReturn.attempts, checkoutReturn.status, checkoutReturn.visible, effectiveTenantBootstrapReady, refreshCheckoutReturnBillingState, user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible || checkoutReturn.status !== 'confirmed') return undefined;
    checkoutReturnNavigationCommittedRef.current = false;
    return undefined;
  }, [checkoutReturn.status, checkoutReturn.visible]);

  useEffect(() => {
    if (user?.uid) {
      checkoutReturnNavigationCommittedRef.current = false;
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!checkoutReturn.visible) return undefined;
    if ((!user?.uid && !hasCheckoutReturnSessionId()) || !effectiveTenantBootstrapReady) return undefined;
    const onFocus = () => { void refreshCheckoutReturnBillingState(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkoutReturn.visible, refreshCheckoutReturnBillingState, effectiveTenantBootstrapReady, user?.uid]);

  const retryWorkspaceBootstrap = useCallback(() => {
    billingAccessRequestIdRef.current += 1;
    tenantBootstrapKeyRef.current = null;
    if (tenantBootstrapTimeoutRef.current != null) {
      window.clearTimeout(tenantBootstrapTimeoutRef.current);
      tenantBootstrapTimeoutRef.current = null;
    }
    setFatalError(null);
    setAuthBootstrapError(null);
    setAuthReady(false);
    setUser(null);
    setTenantBootstrapReady(false);
    setTenantBootstrapSlow(false);
    setTenantMembershipRole(null);
    setTenantBootstrapError(null);
    setBillingState(BILLING_BYPASS_ACTIVE ? BILLING_BYPASS_STATE : null);
    setBillingStateOwnerUid(BILLING_BYPASS_ACTIVE ? BILLING_BYPASS_OWNER_UID : null);
    setBillingReady(BILLING_BYPASS_ACTIVE);
    setBillingError(null);
    setDashboardCommitted(false);
    hasNavigatedRef.current = false;
    setTenantBootstrapAttempt((current) => current + 1);
    setAuthBootstrapAttempt((current) => current + 1);
  }, []);

  const handleTenantBootstrapSignOut = useCallback(() => {
    void signOut()
      .catch(() => undefined)
      .finally(() => {
        try {
          window.location.replace('/login');
        } catch {
          window.location.href = '/login';
        }
      });
  }, []);

  const handleCreateNewJournalEntry = () => {
    setActive('Journal');
    setViewingDate(new Date());
    setIsNewEntry(true);
  };

  const handleNavigateToJournalEntry = (date: Date) => {
    setActive('Journal');
    setViewingDate(date);
    setIsNewEntry(false);
  };

  const syncActivePageFromPath = useCallback((pathname: string) => {
    const nextPage = resolveAuthenticatedPageFromPath(pathname);
    if (nextPage) {
      setActive((current) => current === nextPage ? current : nextPage);
    }
  }, []);

  const syncShellRouteForPage = useCallback((page: PageName) => {
    try {
      const currentPath = normalizeAuthenticatedShellPath(String(window.location.pathname || '/').trim() || '/');
      const nextPath = resolveAuthenticatedPathForPage(page);

      if (!nextPath || nextPath === currentPath) {
        return;
      }

      window.history.pushState({}, '', nextPath);
      setPublicLocation({ path: null, hash: null });
    } catch {}
  }, []);

  const handleNavigate = useCallback((page: PageName) => {
    if (readPublicGuestSession()) {
      const restrictedPages: PageName[] = ['Settings', 'Admin Tools', 'New Trade'];
      if (restrictedPages.includes(page)) {
        if (page !== 'Admin Tools') {
          triggerGuestRestrictedAction(page);
        }
        return;
      }
    }

    if (page === 'New Trade') {
      setCreateCustomOpen(true);
      startTransition(() => {
        setActive('Trades');
        setViewingDate(null);
        setIsNewEntry(false);
      });
      syncShellRouteForPage('Trades');
      return;
    }

    startTransition(() => {
      setActive(page);
      setViewingDate(null);
      setIsNewEntry(false);
    });
    syncShellRouteForPage(page);

    try {
      if (mainRef && mainRef.current) {
        mainRef.current.scrollTo({ top: 0, left: 0 });
      }
    } catch {}
    try { window.scrollTo?.(0, 0); } catch {}
  }, [syncShellRouteForPage]);

  const refreshAdminToolsAccess = useCallback(async () => {
    if (!user?.uid) {
      adminToolsAccessRequestIdRef.current += 1;
      setAdminToolsAccess(null);
      setAdminToolsAccessError(null);
      setAdminToolsAccessReady(false);
      return;
    }

    const requestId = adminToolsAccessRequestIdRef.current + 1;
    adminToolsAccessRequestIdRef.current = requestId;
    setAdminToolsAccessError(null);
    setAdminToolsAccessReady(false);

    try {
      const access = await fetchAdminBillingAccess({
        uid: String(user.uid),
        email: user?.email ? String(user.email) : null,
      }, { correlationId: bootstrapCorrelationIdRef.current || undefined });

      if (adminToolsAccessRequestIdRef.current !== requestId) {
        return;
      }

      setAdminToolsAccess(access);
      setAdminToolsAccessError(null);
    } catch (error: any) {
      if (adminToolsAccessRequestIdRef.current !== requestId) {
        return;
      }

      const status = Number(error?.status || 0);
      const code = String(error?.code || '').trim().toLowerCase();
      if (status === 403 || code === 'admin_forbidden') {
        setAdminToolsAccess(null);
        setAdminToolsAccessError(null);
      } else {
        setAdminToolsAccess(null);
        setAdminToolsAccessError(String(error?.message || 'Unable to verify admin access.').trim() || 'Unable to verify admin access.');
      }
    } finally {
      if (adminToolsAccessRequestIdRef.current === requestId) {
        setAdminToolsAccessReady(true);
      }
    }
  }, [user?.email, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !effectiveTenantBootstrapReady) {
      adminToolsAccessRequestIdRef.current += 1;
      setAdminToolsAccess(null);
      setAdminToolsAccessError(null);
      setAdminToolsAccessReady(false);
      return;
    }

    void refreshAdminToolsAccess();
  }, [effectiveTenantBootstrapReady, refreshAdminToolsAccess, user?.uid]);

  const refreshBusinessAccess = useCallback(async () => {
    if (!user?.uid) {
      setBusinessAccess(null);
      setBusinessAccessReady(false);
      return;
    }
    try {
      const access = await fetchBusinessAdminAccess({
        uid: String(user.uid),
        email: user?.email ? String(user.email) : null,
      });
      setBusinessAccess(access);
    } catch {
      setBusinessAccess(null);
    } finally {
      setBusinessAccessReady(true);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => {
    if (!user?.uid || !effectiveTenantBootstrapReady) {
      setBusinessAccess(null);
      setBusinessAccessReady(false);
      return;
    }
    void refreshBusinessAccess();
  }, [effectiveTenantBootstrapReady, refreshBusinessAccess, user?.uid]);

  useEffect(() => {
    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const normalizedPath = normalizeAuthenticatedShellPath(pathname);
      const isPublicPath = normalizePublicPath(pathname);
      if (readPublicGuestSession()) {
        if (isPublicPath != null && isPublicPath !== ('/demo' as PublicRoutePath)) {
          clearPublicGuestSession();
        } else {
          const restrictedPaths = ['/settings', '/admin/tools', '/trades/new'];
          if (restrictedPaths.includes(normalizedPath)) {
            window.history.replaceState({}, '', '/dashboard');
            setActive('Dashboard');
            if (normalizedPath !== '/admin/tools') {
              triggerGuestRestrictedAction(resolveAuthenticatedPageFromPath(normalizedPath) || 'Restricted Feature');
            }
          }
        }
      }
    } catch {}
  }, [triggerGuestRestrictedAction]);

  useEffect(() => {
    try {
      syncActivePageFromPath(String(window.location.pathname || '/').trim() || '/');
    } catch {}
  }, [syncActivePageFromPath, user?.uid]);

  useEffect(() => {
    if (active === 'Admin Tools' && adminToolsAccessReady && !showAdminToolsNav) {
      handleNavigate('Dashboard');
    }
  }, [active, adminToolsAccessReady, showAdminToolsNav, handleNavigate]);

  useEffect(() => {
    // Direct /business URL access is protected server-side; if the access check
    // finished and the user is not authorized, redirect away from the page.
    if (active === 'Business' && businessAccessReady && !showBusinessNav) {
      handleNavigate('Dashboard');
    }
  }, [active, businessAccessReady, showBusinessNav, handleNavigate]);

  const handleFirstSessionImportTrades = useCallback(() => {
    completeFirstSessionOnboarding();
    handleNavigate('Import Trades');
  }, [completeFirstSessionOnboarding, handleNavigate]);

  const handleBackFromTradeDetails = useCallback(() => {
    let backTarget: PageName = 'Trades';
    try {
      const storedTarget = String(sessionStorage.getItem(TRADE_DETAILS_BACK_TARGET_KEY) ?? '').trim();
      if (storedTarget && storedTarget !== 'Trade Details') {
        backTarget = storedTarget as PageName;
      }
    } catch {}

    try { sessionStorage.removeItem(TRADE_DETAILS_BACK_TARGET_KEY); } catch {}
    setSelectedTrade(null);
    handleNavigate(backTarget);
  }, [handleNavigate]);

  const navigatePublic = useCallback((nextPath: PublicRoutePath, options?: { hash?: string | null }) => {
    const nextHash = options?.hash ? `#${options.hash}` : '';
    try {
      const nextUrl = `${nextPath}${nextHash}`;
      if (`${window.location.pathname}${window.location.hash}` !== nextUrl) {
        window.history.pushState({}, '', nextUrl);
      }
    } catch {}
    // Clear any stale signup/checkout state when explicitly navigating to the signup surface
    try {
      if (nextPath === '/signup') {
        try { clearSignupCheckoutHandoff('public_navigate_signup'); } catch {}
        try { clearBillingNotice(); } catch {}
        try { clearLoginAuthErrorNotice(); } catch {}
        try { clearLogoutTransitionNotice(); } catch {}
      }
    } catch {}
    setPublicLocation({ path: nextPath, hash: options?.hash || null, explicit: true });
    try { window.scrollTo?.({ top: 0, left: 0, behavior: 'auto' }); } catch {}
  }, [clearBillingNotice, clearLoginAuthErrorNotice, clearLogoutTransitionNotice, clearSignupCheckoutHandoff]);

  const navigateToTenantLanding = useCallback(() => {
    const landingUrl = currentRuntime.brand.landingUrl;

    if (!landingUrl || landingUrl === '/') {
      navigatePublic('/');
      return;
    }

    if (/^https?:\/\//i.test(landingUrl)) {
      window.location.href = landingUrl;
      return;
    }

    if (landingUrl.startsWith('/')) {
      navigatePublic(landingUrl as PublicRoutePath);
      return;
    }

    try {
      navigatePublic(landingUrl as PublicRoutePath);
    } catch {}
  }, [navigatePublic]);
  const abandonedCheckoutMainSiteUrl = currentRuntime.brand.id === 'warrior-trading'
    ? 'https://warriortrading.com'
    : currentRuntime.brand.landingUrl;

  useEffect(() => {
    if (!authReady) return;

    try {
      const billingReturnKind = readBillingReturnKindFromLocation();
      if (signupCheckoutHandoffActive && !billingReturnKind) return;
      const hasDashboardAccess = BILLING_BYPASS_ACTIVE || (billingStateOwnerUid === user?.uid && hasConfirmedBillingAccess(billingState));

      const pathname = String(window.location.pathname || '/').trim() || '/';
      const shellPathname = normalizeAuthenticatedShellPath(pathname);
      const publicPath = normalizePublicPath(pathname);
      if (isPublicShellRoutePath(pathname)) return;
      const search = String(window.location.search || '');
      const hash = String(window.location.hash || '');
      const billing = String(new URLSearchParams(search).get('billing') || '').trim().toLowerCase();
      const hasStoredIdentityDuringRehydration = !isLogoutTransitionActive && Boolean(getStoredAppIdentity()?.uid);
      const isSessionRecoveryPending = !user
        && hasStoredIdentityDuringRehydration
        && !loginAuthError
        && !checkoutReturn.visible
        && (shellPathname === DASHBOARD_PATHNAME || shellPathname === ADMIN_TOOLS_PATHNAME || publicPath == null);
      const isCheckoutSuccessReturn = billing === 'success';
      const isCheckoutCancelledReturn = billing === 'cancelled' || billing === 'canceled';
      const isSignupCheckoutHandoffRoute = loginFlowActive && (pathname === '/signup' || publicPath === ('/signup' as PublicRoutePath));

      if (isSignupCheckoutHandoffRoute) {
        return;
      }

      if (isSessionRecoveryPending) {
        return;
      }

      if (!user && isLogoutTransitionActive) {
        if (pathname !== '/login' || publicPath !== ('/login' as PublicRoutePath)) {
          window.history.replaceState({}, '', '/login');
          setPublicLocation({ path: '/login', hash: null });
        }
        return;
      }

      if (!user && (shellPathname === DASHBOARD_PATHNAME || shellPathname === ADMIN_TOOLS_PATHNAME)) {
        if (readPublicGuestSession()) return;
        window.history.replaceState({}, '', '/login');
        setPublicLocation({ path: '/login', hash: null });
        return;
      }

      if (!user && publicPath === null) {
        if (isCheckoutReturnWorkspaceCommit) {
          return;
        }
        if (readPublicGuestSession()) return;
        window.history.replaceState({}, '', '/');
        setPublicLocation({ path: '/', hash: null });
        return;
      }

      if (user && shellPathname === ADMIN_TOOLS_PATHNAME) {
        setPublicLocation({ path: null, hash: null });
        try { setActive('Admin Tools'); } catch {}
        return;
      }

      if (user && pathname === '/' && !hash && !publicLocation.explicit && !checkoutReturn.visible && !isCheckoutCancelledReturn && hasDashboardAccess) {
        window.history.replaceState({}, '', `/dashboard${search}${hash}`);
        setPublicLocation({ path: null, hash: null });
        try { setActive('Dashboard'); } catch {}
        return;
      }

      if (user && pathname === '/login' && effectiveTenantBootstrapReady && hasDashboardAccess && !isLogoutTransitionActive && !isCheckoutSuccessReturn && !isCheckoutCancelledReturn) {
        if (loginSuccessMessage) return;
        window.history.replaceState({}, '', '/dashboard');
        setPublicLocation({ path: null, hash: null });
        try { setActive('Dashboard'); } catch {}
        clearSignupCheckoutHandoff();
      }

      if (user && currentRuntime.brand.frontDoor === 'login' && hasDashboardAccess) {
        const isPublicStylePath = pathname !== '/'
          && pathname !== '/login'
          && pathname !== '/signup'
          && pathname !== '/auth/callback'
          && pathname !== '/forgot-password'
          && pathname !== '/auth/reset-password'
          && resolveAuthenticatedPageFromPath(pathname) == null;
        if (isPublicStylePath) {
          window.history.replaceState({}, '', '/dashboard');
          setPublicLocation({ path: null, hash: null });
          try { setActive('Dashboard'); } catch {}
        }
      }
    } catch {}
  }, [authReady, billingState?.hasAccess, billingState?.isAdminBillingBypass, billingStateOwnerUid, checkoutReturn.visible, clearSignupCheckoutHandoff, effectiveTenantBootstrapReady, isLogoutTransitionActive, loginAuthError, loginFlowActive, loginSuccessMessage, publicLocation.explicit, publicLocation.path, user]);

  useEffect(() => {
    if (!authReady) return;

    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      const livePublicPath = normalizePublicPath(pathname);

      if (livePublicPath === ('/auth/callback' as PublicRoutePath) || publicLocation.path === ('/auth/callback' as PublicRoutePath)) return;

      if (!user?.uid && loginFlowActive && !authIntentPending) {
        clearSignupCheckoutHandoff();
      }
    } catch {
      if (!user?.uid && loginFlowActive && !authIntentPending && String(publicLocation.path) !== '/auth/callback') {
        clearSignupCheckoutHandoff();
      }
    }
  }, [authIntentPending, authReady, clearSignupCheckoutHandoff, loginFlowActive, publicLocation.path, user?.uid]);

  useEffect(() => {
    if (!loginAuthError) return;
    if (lastLoggedDeniedNonceRef.current === loginAuthError.nonce) return;

    lastLoggedDeniedNonceRef.current = loginAuthError.nonce;
    const renderedAt = readNow();
    tpStripeFlashLog('denial_rendered', {
      authToBootstrapStartMs: roundDurationMs(authReadyForBootstrapAtRef.current == null || bootstrapStartedAtRef.current == null ? null : bootstrapStartedAtRef.current - authReadyForBootstrapAtRef.current),
      sessionLookupMs: deniedTimingsRef.current?.sessionLookupMs ?? null,
      bootstrapDurationMs: deniedTimingsRef.current?.bootstrapDurationMs ?? null,
      totalToErrorMs: roundDurationMs(authReadyForBootstrapAtRef.current == null ? null : renderedAt - authReadyForBootstrapAtRef.current),
      responseToErrorMs: roundDurationMs(deniedResponseAtRef.current == null ? null : renderedAt - deniedResponseAtRef.current),
    });
  }, [loginAuthError]);

  // Expose setter globally as a fallback for UI wired outside React tree
  useEffect(() => {
    try {
      (window as any).__TT_setNavLayout = setNavLayout;
      (window as any).__TT_navigate = handleNavigate;
      (window as any).__TT_viewTrade = (trade: any) => {
        try {
          if (trade) {
            writePrivateTradingJson('last-view-trade', JSON.stringify(trade), 'session');
          }
        } catch {}
        try {
          const sourcePage = active && active !== 'Trade Details' ? active : 'Trades';
          sessionStorage.setItem(TRADE_DETAILS_BACK_TARGET_KEY, String(sourcePage));
        } catch {}
        try { setSelectedTrade(trade); } catch {}
        try { handleNavigate('Trade Details'); } catch {}
      };
      (window as any).__TT_reviewOnboardingTrade = () => {
        const trade = authoritativeTrades[0];
        if (!trade) return;
        try {
          writePrivateTradingJson('last-view-trade', JSON.stringify(trade), 'session');
          sessionStorage.setItem(TRADE_DETAILS_BACK_TARGET_KEY, String(active && active !== 'Trade Details' ? active : 'Trades'));
        } catch {}
        // Select the real authoritative trade here; the tour's Next handler
        // owns the single route transition that follows.
        setSelectedTrade(trade as Trade);
      };
    } catch {}
    return () => {
      try { delete (window as any).__TT_setNavLayout; } catch {}
      try { delete (window as any).__TT_navigate; } catch {}
      try { delete (window as any).__TT_viewTrade; } catch {}
      try { delete (window as any).__TT_reviewOnboardingTrade; } catch {}
    };
  }, [active, authoritativeTrades, handleNavigate]);

  useEffect(() => {
    try {
      // Temporarily disable CSS transitions/animations to make the theme flip instantaneous
      document.documentElement.classList.add('disable-theme-transitions');
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      // Force a reflow to ensure the class change takes effect immediately
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.documentElement.offsetHeight;
      // Re-enable transitions on the next frame
      window.setTimeout(() => {
        try { document.documentElement.classList.remove('disable-theme-transitions'); } catch {}
      }, 0);
    } catch {}
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  // Public, pre-auth entry flow uses a lightweight path switch without changing the authenticated shell.
  const publicPath = publicLocation.path;
  const livePathname = (() => {
    try {
      return String(window.location.pathname || '/').trim() || '/';
    } catch {
      return '/';
    }
  })();
  const livePublicPath = normalizePublicPath(livePathname);
  // Canonical tenant-host routing. A hostname that is a tenant wildcard or a
  // resolved tenant branding candidate must never render the Trade-Proof marketing
  // page: root `/` (and other marketing shell paths) redirect to `/login`, and
  // unknown tenant hostnames get a controlled Tenant Not Found surface.
  const tenantHostname = String(typeof window !== 'undefined' ? window.location.hostname || '' : '').trim().toLowerCase();
  const tenantHostClass = classifyTenantHostname(tenantHostname);
  const tenantBrandingPending = publicTenantState === null && tenantHostClass !== 'base';
  const isTenantHost = tenantHostClass !== 'base' && (tenantHostClass === 'tenant_wildcard' || publicTenantState != null);
  const isTenantNotFoundHost = isTenantHost && publicTenantState === 'not_found';
  const isTenantMarketingPath = isTenantHostMarketingPath(livePathname);
  const isLegalAliasRoute = /^\/(legal|terms|terms-of-service|privacy|privacy-policy)\/?$/i.test(livePathname);

  // Canonical tenant-host root redirect. For a valid tenant hostname the root `/`
  // (and other marketing shell paths) redirect to `/login`, preserving the query
  // string. Unknown tenants render the controlled Tenant Not Found surface instead.
  // Valid tenant routes (/login, /dashboard, /trades, ...) are never redirected.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isTenantHost || isTenantNotFoundHost) return;
    if (tenantBrandingPending) return;

    try {
      const pathname = String(window.location.pathname || '/').trim() || '/';
      if (!isTenantHostMarketingPath(pathname)) return;
      if (pathname === '/login') return;

      const search = window.location.search || '';
      window.history.replaceState({}, '', `/login${search}`);
      setPublicLocation({ path: '/login', hash: null });
    } catch {
      // Never let the redirect effect crash the app shell.
    }
  }, [isTenantHost, isTenantNotFoundHost, setPublicLocation, tenantBrandingPending]);
  const isAlwaysPublicRoute = isLegalAliasRoute || publicPath === ('/legal' as PublicRoutePath) || livePublicPath === ('/legal' as PublicRoutePath);
  const liveShellPath = normalizeAuthenticatedShellPath(livePathname);
  const isAdminToolsRoute = liveShellPath === ADMIN_TOOLS_PATHNAME;
  const publicSitePath: Exclude<PublicRoutePath, '/login' | '/signup'> = (
    publicPath && publicPath !== ('/login' as PublicRoutePath) && publicPath !== ('/signup' as PublicRoutePath)
      ? publicPath
      : livePublicPath && livePublicPath !== ('/login' as PublicRoutePath) && livePublicPath !== ('/signup' as PublicRoutePath)
        ? livePublicPath
      : '/'
  ) as Exclude<PublicRoutePath, '/login' | '/signup'>;
  const isCheckoutReturnWorkspaceCommit = checkoutReturnNavigationCommittedRef.current;
  const billingReturnKind = readBillingReturnKindFromLocation();
  const isBillingSuccessReturn = billingReturnKind === 'success';
  const isBillingCancelledReturn = billingReturnKind === 'cancelled' || billingReturnKind === 'canceled';
  const isBillingReturnRoute = isBillingSuccessReturn || isBillingCancelledReturn;
  const billingEnforcementActive = !BILLING_BYPASS_ACTIVE;
  const billingStateBelongsToCurrentUser = BILLING_BYPASS_ACTIVE || Boolean(user?.uid && billingState && billingStateOwnerUid === user.uid);
  const billingAccessGranted = BILLING_BYPASS_ACTIVE || (billingStateBelongsToCurrentUser && hasConfirmedBillingAccess(billingState));
  const isSignupRoute = publicPath === ('/signup' as PublicRoutePath) || livePublicPath === ('/signup' as PublicRoutePath);
  const isLoginRoute = publicPath === ('/login' as PublicRoutePath)
    || publicPath === ('/signup' as PublicRoutePath)
    || livePublicPath === ('/login' as PublicRoutePath)
    || livePublicPath === ('/signup' as PublicRoutePath);
  const isExplicitLoginEntryRoute = publicPath === ('/login' as PublicRoutePath) || livePublicPath === ('/login' as PublicRoutePath);
  const isFrontDoorRootEntryRoute = currentRuntime.brand.frontDoor === 'login'
    && (publicPath === ('/' as PublicRoutePath) || livePublicPath === ('/' as PublicRoutePath));
  const isCleanLoginEntryRoute = !isBillingReturnRoute && !isSignupRoute && (isExplicitLoginEntryRoute || isFrontDoorRootEntryRoute);
  const loginInitialMode = (publicPath === ('/signup' as PublicRoutePath) || livePublicPath === ('/signup' as PublicRoutePath)) ? 'signup' : 'login';
  const isAuthenticated = Boolean(user?.uid);
  const storedAuthenticatedIdentity = (() => {
    if (isLogoutTransitionActive) return null;
    try {
      return getStoredAppIdentity();
    } catch {
      return null;
    }
  })();
  const hasStoredAuthenticatedIdentity = Boolean(storedAuthenticatedIdentity?.uid);
  const isAuthenticatedShellRoute = publicPath == null || resolveAuthenticatedPageFromPath(livePathname) != null;
  const isAuthSessionRecoveryPending = authReady
    && !isAuthenticated
    && hasStoredAuthenticatedIdentity
    && !loginAuthError
    && !checkoutReturn.visible
    && !isLogoutTransitionActive
    && isAuthenticatedShellRoute;
  const isCheckoutReturnSessionRecoveryPending = checkoutReturn.visible && checkoutReturn.status === 'pending' && !user?.uid;
  const isAccessCheckPending = isAuthenticated && isLoginRoute && !loginAuthError && !effectiveTenantBootstrapReady && !tenantBootstrapError;
  const isAuthorizedBootstrapPending = isAuthenticated && !isLoginRoute && !effectiveTenantBootstrapReady && !tenantBootstrapError;
  const isWorkspaceBootstrapPending = isAuthenticated && (
    isAccessCheckPending
    || isAuthorizedBootstrapPending
    || (effectiveTenantBootstrapReady && !adminToolsAccessReady)
  );
  const isSignupCheckoutHandoffActive = !isBillingReturnRoute && signupCheckoutHandoffActive;
  const shouldStayOnLoginSurface = isLoginRoute && !isAuthenticated && isBillingCancelledReturn;
  const shouldReleaseAuthenticatedAbandonedCheckoutToLogin = !loginFlowActive
    && isCleanLoginEntryRoute
    && Boolean(user?.uid)
    && effectiveTenantBootstrapReady
    && billingEnforcementActive
    && billingStateBelongsToCurrentUser
    && !billingAccessGranted
    && isAbandonedCheckoutState(billingState)
    && !checkoutReturn.visible;
  const renderBranch = (() => {
    if (isAlwaysPublicRoute) return 'public_surface';
    if (!authReady) return 'loading';
    if (fatalError) return 'fatal_error';
    if (isLogoutTransitionActive) return 'login_surface';
    if (isSignupCheckoutHandoffActive) return 'signup_checkout_handoff';
    if (isSignupRoute) return isSignupCheckoutHandoffActive ? 'signup_checkout_handoff' : 'signup_surface';
    if (loginAuthError) return 'login_auth_error';
    if (checkoutReturn.visible) return 'checkout_return';
    if (shouldStayOnLoginSurface) return 'login_surface';
    if (shouldReleaseAuthenticatedAbandonedCheckoutToLogin) return 'billing_gate';
    if (isAccessCheckPending || isAuthSessionRecoveryPending) return 'login_access_check_pending';
    if (!user?.uid) {
      const isPublicMarketingOrAuth = publicPath != null && publicPath !== ('/demo' as PublicRoutePath);
      if (isPublicGuest && !isPublicMarketingOrAuth) return 'dashboard';
      if (isCheckoutReturnWorkspaceCommit) return 'checkout_workspace_transition';
      if (publicPath === ('/login' as PublicRoutePath) || publicPath === ('/signup' as PublicRoutePath) || ((publicPath === ('/' as PublicRoutePath) || publicPath == null) && currentRuntime.brand.frontDoor === 'login')) {
        return 'login_surface';
      }
      if (isAlwaysPublicRoute) return 'public_surface';
      return currentRuntime.brand.frontDoor === 'login' ? 'login_surface' : 'public_surface';
    }
    if (isAuthorizedBootstrapPending) return 'tenant_bootstrap_pending';
    if (isAuthenticated && effectiveTenantBootstrapReady && !adminToolsAccessReady) return 'permission_check_pending';
    if (tenantBootstrapError) return 'tenant_bootstrap_error';
    if (publicPath && (publicPath !== '/' || publicLocation.hash || publicLocation.explicit) && String(publicPath) !== '/login' && String(publicPath) !== '/signup' && currentRuntime.brand.frontDoor !== 'login') return 'public_surface';
    const shouldRouteBlockedLoginToBillingGate = isLoginRoute && Boolean(user?.uid) && loginFlowActive;
    if (billingEnforcementActive && !billingStateBelongsToCurrentUser && !isAdminToolsRoute && (isAuthenticatedShellRoute || shouldRouteBlockedLoginToBillingGate)) return 'billing_access_check_pending';
    if (billingEnforcementActive && !billingAccessGranted && !isAdminToolsRoute && (isAuthenticatedShellRoute || shouldRouteBlockedLoginToBillingGate)) return 'billing_gate';
    return 'dashboard';
  })();

  useEffect(() => {
    if (!authReady || !isCleanLoginEntryRoute) return;

    const record = readSignupCheckoutHandoffRecord();
    if (!record && !signupCheckoutHandoffActive) return;

    clearSignupCheckoutHandoff('clean_login_entry');
  }, [authReady, clearSignupCheckoutHandoff, isCleanLoginEntryRoute, signupCheckoutHandoffActive]);

  useEffect(() => {
    if (!authReady || !shouldReleaseAuthenticatedAbandonedCheckoutToLogin) {
      cleanLoginEscapeSignatureRef.current = null;
      return;
    }

    const signature = `${user?.uid || 'anonymous'}:${livePathname}`;
    if (cleanLoginEscapeSignatureRef.current === signature) return;

    cleanLoginEscapeSignatureRef.current = signature;
    // For clean login entries where an authenticated user is detected but
    // billing indicates an abandoned checkout, release the session back to
    // the login surface by clearing handoff state, beginning the logout
    // transition, and signing out locally. This prevents long-lived
    // authenticated shells from persisting on the login route.
    try {
      clearSignupCheckoutHandoff('release_authenticated_abandoned_checkout');
    } catch {}
    try { clearBillingNotice(); } catch {}
    try { beginLogoutTransition(); } catch {}
    void signOut({ scope: 'local' }).catch(() => undefined);
  }, [authReady, livePathname, shouldReleaseAuthenticatedAbandonedCheckoutToLogin, user?.uid]);
  const workspaceLoadPending = !fatalError && !isAlwaysPublicRoute && (
    !authReady
    || renderBranch === 'login_access_check_pending'
    || renderBranch === 'tenant_bootstrap_pending'
    || renderBranch === 'billing_access_check_pending'
  );

  useEffect(() => {
    if (!user?.uid || !effectiveTenantBootstrapReady || !billingAccessGranted || checkoutReturn.visible) return;

    try {
      const pathname = normalizeAuthenticatedShellPath(String(window.location.pathname || '/').trim() || '/');
      const onAuthenticatedShell = pathname === DASHBOARD_PATHNAME || pathname === ADMIN_TOOLS_PATHNAME || publicLocation.path == null;
      if (!onAuthenticatedShell) return;
    } catch {
      if (publicLocation.path != null) {
        return;
      }
    }

    clearSignupCheckoutHandoff('authenticated_workspace_access');
  }, [billingAccessGranted, checkoutReturn.visible, clearSignupCheckoutHandoff, effectiveTenantBootstrapReady, publicLocation.path, user?.uid]);

  useEffect(() => {
    if (!workspaceLoadPending) return undefined;

    const timer = window.setTimeout(() => {
      const snapshot = {
        renderBranch,
        authReady,
        userUid: user?.uid || null,
        publicPath,
        loginFlowActive,
        tenantBootstrapReady,
        tenantBootstrapSlow,
        effectiveTenantBootstrapReady,
        tenantBootstrapError: tenantBootstrapError || null,
        tenantBootstrapAttempt,
        billingReady,
        billingError: billingError || null,
        billingStatus: billingState?.subscriptionStatus ?? null,
        hasBillingState: !!billingState,
        checkoutReturnVisible: checkoutReturn.visible,
        checkoutReturnStatus: checkoutReturn.status,
        correlationId: bootstrapCorrelationIdRef.current,
      };

      tpStripeFlashLog('workspace_loading_stall', snapshot);
      try {
        console.warn('[TP] workspace loading stall', snapshot);
      } catch {}
    }, WORKSPACE_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [WORKSPACE_LOAD_TIMEOUT_MS, authReady, billingError, billingReady, billingState, checkoutReturn.status, checkoutReturn.visible, effectiveTenantBootstrapReady, fatalError, loginFlowActive, publicPath, renderBranch, tenantBootstrapAttempt, tenantBootstrapError, tenantBootstrapReady, tenantBootstrapSlow, user?.uid, workspaceLoadPending]);

  const shouldUseWorkspaceOpeningSurface = !isLogoutTransitionActive && !isAlwaysPublicRoute && (
    Boolean(user?.uid)
    || Boolean(resolveStoredIdentityFallback())
    || Boolean(loginFlowActive)
    || livePathname === '/dashboard'
    || resolveAuthenticatedPageFromPath(livePathname) != null
  );

  const isWorkspaceLoaderBranch = (() => {
    if (isAlwaysPublicRoute) return false;
    if (isLogoutTransitionActive) return false;
    if (fatalError) return false;

    if (!authReady) {
      if ((publicPath && publicPath !== ('/login' as PublicRoutePath) && publicPath !== ('/signup' as PublicRoutePath)) || (livePublicPath && livePublicPath !== ('/login' as PublicRoutePath) && livePublicPath !== ('/signup' as PublicRoutePath))) {
        return false;
      }
      if (checkoutReturn.visible) {
        return false;
      }
      if (isLoginRoute) {
        return false;
      }
      return shouldUseWorkspaceOpeningSurface;
    }

    if (checkoutReturn.visible) {
      return false;
    }
    if (!user?.uid && isCheckoutReturnWorkspaceCommit) {
      return true;
    }
    if (isSignupCheckoutHandoffActive) return false;
    if (isSignupRoute) return false;
    if (loginAuthError) return false;
    if (isBillingCancelledReturn && isLoginRoute) return false;
    if (renderBranch === 'login_surface') {
      if (loginFlowActive || authIntentPending) {
        return true;
      }
      return false;
    }

    if (renderBranch === 'login_access_check_pending' || renderBranch === 'tenant_bootstrap_pending' || renderBranch === 'permission_check_pending' || renderBranch === 'billing_access_check_pending' || renderBranch === 'tenant_bootstrap_error' || tenantBootstrapError != null) {
      return true;
    }
    if (renderBranch === 'dashboard' && !dashboardCommitted) {
      return true;
    }
    return false;
  })();

  const startupReadiness = useMemo(() => {
    const isPendingRenderBranch = renderBranch === 'loading'
      || renderBranch === 'login_access_check_pending'
      || renderBranch === 'tenant_bootstrap_pending'
      || renderBranch === 'permission_check_pending'
      || renderBranch === 'billing_access_check_pending';
    const routeReady = authReady
      && !authBootstrapError
      && !fatalError
      && !isPendingRenderBranch
      && !tenantBootstrapError
      && !billingError;
    const statusLabel = !authReady
      ? 'Checking session'
      : user?.uid && !effectiveTenantBootstrapReady
      ? 'Loading workspace'
      : renderBranch === 'billing_access_check_pending'
      ? 'Checking access'
      : renderBranch === 'dashboard' && !dashboardCommitted
      ? 'Preparing dashboard'
      : 'Preparing workspace';
    const progress = calculateBootstrapProgress({
      authReady,
      hasAuthenticatedUser: Boolean(user?.uid),
      tenantReady: effectiveTenantBootstrapReady,
      billingReady,
      routeReady,
    });

    return {
      statusLabel,
      progress,
      routeReady,
    };
  }, [
    authBootstrapError,
    authReady,
    billingError,
    billingReady,
    dashboardCommitted,
    effectiveTenantBootstrapReady,
    fatalError,
    renderBranch,
    tenantBootstrapError,
    user?.uid,
  ]);

  useEffect(() => {
    if (renderBranch !== 'dashboard') {
      setDashboardCommitted(false);
      hasNavigatedRef.current = false;
    }
  }, [renderBranch]);

  useEffect(() => {
    if (previousRenderBranchRef.current === renderBranch) return;
    tpStripeFlashLog('render_branch_changed', {
      renderBranch,
      readyToRenderDashboard: renderBranch === 'dashboard',
      readyToRenderApp: renderBranch === 'dashboard',
      routeDecision: renderBranch,
      authReady,
      userUid: user?.uid || null,
      publicPath,
      loginFlowActive,
      tenantBootstrapReady,
      billingReady,
      billingStatus: billingState?.subscriptionStatus ?? null,
      hasAccess: billingState?.hasAccess ?? null,
      checkoutReturnVisible: checkoutReturn.visible,
      isSignupCheckoutHandoffActive,
      billingReturnKind: billingReturnKind || null,
      billingRequired: renderBranch === 'billing_gate',
      checkoutRedirectPending: renderBranch === 'checkout_return' && checkoutReturn.status === 'pending',
      correlationId: bootstrapCorrelationIdRef.current,
      ts: Date.now(),
    });
    if (renderBranch === 'dashboard') {
      tpStripeFlashLog('ready_to_render_app_dashboard', {
        authReady,
        userUid: user?.uid || null,
        publicPath,
        loginFlowActive,
        tenantBootstrapReady,
        billingReady,
        billingStatus: billingState?.subscriptionStatus ?? null,
        hasAccess: billingState?.hasAccess ?? null,
        correlationId: bootstrapCorrelationIdRef.current,
      });
    }
    if (renderBranch === 'billing_gate') {
      tpStripeFlashLog('billing_required_decision', {
        authReady,
        userUid: user?.uid || null,
        publicPath,
        loginFlowActive,
        tenantBootstrapReady,
        billingReady,
        billingStatus: billingState?.subscriptionStatus ?? null,
        hasAccess: billingState?.hasAccess ?? null,
      });
    }
    if (renderBranch === 'checkout_return') {
      tpStripeFlashLog('checkout_redirect_pending', {
        authReady,
        userUid: user?.uid || null,
        publicPath,
        checkoutReturnVisible: checkoutReturn.visible,
        checkoutReturnStatus: checkoutReturn.status,
        attempts: checkoutReturn.attempts,
      });
    }
    previousRenderBranchRef.current = renderBranch;
  }, [authReady, billingReady, billingReturnKind, billingState?.hasAccess, billingState?.subscriptionStatus, billingStateBelongsToCurrentUser, checkoutReturn.attempts, checkoutReturn.status, checkoutReturn.visible, checkoutReturnSessionRecovery.failed, isCheckoutReturnSessionRecoveryPending, isLogoutTransitionActive, isSignupCheckoutHandoffActive, loginFlowActive, publicPath, renderBranch, signupCheckoutHandoffActive, tenantBootstrapReady, user?.uid]);

  const renderLoginPage = (authInProgress: boolean) => (
    <LoginPage
      key={`login-page:${loginInitialMode}`}
      initialMode={loginInitialMode}
      onBackToLanding={navigateToTenantLanding}
      authErrorMessage={loginInitialMode === 'signup' ? null : (loginAuthError?.message ?? null)}
      authSuccessMessage={loginSuccessMessage?.message ?? null}
      noticeMessage={
        publicTenantState === 'suspended'
          ? "This tenant's website is temporarily unavailable."
          : publicTenantState === 'not_found'
            ? 'Tenant not found for this hostname.'
            : (billingNotice?.message ?? null)
      }
      authInProgress={authInProgress}
      checkoutHandoffActive={isSignupCheckoutHandoffActive}
      onCheckoutRedirectStart={markSignupCheckoutRedirectStart}
      onForgotPassword={() => navigatePublic('/forgot-password')}
      onAuthIntent={() => {
        clearLoginSuccessNotice();
        clearBillingNotice();
        replaceCheckoutReturnLocation();
        if (!user?.uid && isLogoutTransitionActive) {
          setLogoutTransitionPendingState(false);
          clearLogoutTransitionPending();
        }
        setAuthIntentPendingState(true);
        setLoginFlowActiveState(true);
        setLoginFlowActive();
      }}
      onAuthAttemptFailed={() => {
        setAuthIntentPendingState(false);
      }}
      onAuthSuccess={(session) => {
        void recordDemoLoginSuccess(session);
      }}
      onSignupCheckoutStart={beginSignupCheckoutHandoff}
      onSignupCheckoutEnd={clearSignupCheckoutHandoff}
      whiteLabel={whiteLabelAppBranding}
      publicRegistrationEnabled={publicRegistrationEnabled}
    />
  );

function CheckoutLoadingCard({ title, bodyLines }: { title: string; bodyLines: string[] }) {
  return (
    <div className="w-full rounded-2xl border border-slate-700/50 bg-slate-800 p-8 text-center shadow-2xl">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-400">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
      </div>

      <h1 className="mt-6 text-xl font-semibold tracking-tight text-slate-50">
        {title}
      </h1>

      <div className="mt-3 space-y-1.5">
        {bodyLines.map((line) => (
          <p key={line} className="text-sm leading-6 text-slate-400">
            {line}
          </p>
        ))}
      </div>

      <div className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-700/50 bg-slate-900/50 px-4 py-3.5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
        </span>
        <span className="text-sm font-medium text-slate-300">
          Still working securely…
        </span>
      </div>
    </div>
  );
}

  const renderCheckoutReturnSurface = () => {
    const isPending = checkoutReturn.status === 'pending';
    const isSessionPending = isPending && !user?.uid;
    const isConfirmed = checkoutReturn.status === 'confirmed';
    const isFailed = checkoutReturn.status === 'failed';
    const isSessionFailure = isFailed && checkoutReturn.failureReason === 'session';

    const pendingSignupEmail = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tt_pending_signup_email') : null;
    if (isSessionFailure && pendingSignupEmail) {
      const pendingSignupPassword = sessionStorage.getItem('tt_pending_signup_password') || undefined;
      return (
        <PaymentSuccessModal
          email={pendingSignupEmail}
          password={pendingSignupPassword}
          onClose={() => {
            try { sessionStorage.removeItem('tt_pending_signup_email'); } catch {}
            try { sessionStorage.removeItem('tt_pending_signup_password'); } catch {}
            exitCheckoutReturnFailure();
          }}
          onSuccessRedirect={() => {
            try { sessionStorage.removeItem('tt_pending_signup_email'); } catch {}
            try { sessionStorage.removeItem('tt_pending_signup_password'); } catch {}
            exitCheckoutReturnFailure();
          }}
        />
      );
    }

      // When checkout is confirmed, show the success modal with dashboard CTA.
      if (isConfirmed) {
        return (
          <PaymentSuccessModal
            onClose={() => continueCheckoutReturnToWorkspace('cta')}
            onSuccessRedirect={() => continueCheckoutReturnToWorkspace('cta')}
          />
        );
      }

    const isTrialConfirmed = isConfirmed && billingState?.subscriptionStatus === 'trialing';
    const title = isConfirmed
      ? 'You’re all set'
      : isFailed
        ? (isSessionFailure ? 'Payment received' : 'We couldn’t confirm your access yet')
        : 'Setting up your workspace…';
    const bodyLines = isConfirmed
      ? (isTrialConfirmed
        ? ['Your 7-day trial has started.', 'You won’t be charged today.']
        : ['Your subscription is active.', 'Continue to your workspace.'])
      : isFailed
        ? [
          isSessionFailure
            ? 'Please sign in with the email used at checkout to activate access.'
            : 'Your payment completed, but we could not confirm workspace access yet.',
        ]
        : [
          isSessionPending
            ? 'We’re reconnecting your account.'
            : 'This usually takes just a few seconds.',
        ];
    const failureDetail = isSessionFailure
      ? null
      : 'Retry confirmation to check billing again, or open billing if you are already signed in.';

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] px-4 py-5 text-slate-200 sm:px-6 sm:py-8">
        <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] max-w-[22rem] items-center justify-center sm:max-w-[24rem]">
          {!isConfirmed && !isFailed ? (
            <CheckoutLoadingCard title={title} bodyLines={bodyLines} />
          ) : (
            <div className="w-full rounded-2xl border border-slate-700/45 bg-slate-950/82 px-5 py-5 text-center shadow-[0_24px_80px_rgba(2,6,23,0.55)] backdrop-blur-sm sm:px-6 sm:py-6">
              <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-white/10 ${isConfirmed ? 'bg-emerald-500/15 text-emerald-200' : isFailed || isSessionFailure ? 'bg-amber-500/15 text-amber-200' : 'bg-blue-500/15 text-blue-200'}`}>
                <span className="text-lg font-semibold leading-none">{isConfirmed ? '✓' : isFailed || isSessionFailure ? '!' : '…'}</span>
              </div>

              <h1 className="mt-4 text-[1.35rem] font-semibold tracking-[-0.02em] text-slate-50 sm:text-2xl">
                {title}
              </h1>

              <div className="mt-3 space-y-1.5">
                {bodyLines.map((line, index) => (
                  <p
                    key={line}
                    className={isConfirmed && index === 0
                      ? 'text-sm font-medium leading-6 text-slate-100'
                      : 'text-sm leading-6 text-slate-400'}
                  >
                    {line}
                  </p>
                ))}
              </div>

              {isFailed && failureDetail ? (
                <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-left text-sm leading-5 text-amber-100/90">
                  {failureDetail}
                </div>
              ) : null}

              <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
                {isConfirmed ? (
                  <button
                    type="button"
                    onClick={() => continueCheckoutReturnToWorkspace('cta')}
                    className="w-full rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 sm:w-auto sm:min-w-[11.5rem]"
                  >
                    Continue to workspace
                  </button>
                ) : isFailed ? (
                  <>
                    {!isSessionFailure && (
                      <button
                        type="button"
                        onClick={retryCheckoutVerification}
                        className="w-full rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500 sm:w-auto sm:min-w-[10.5rem]"
                      >
                        Retry confirmation
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={exitCheckoutReturnFailure}
                      className={isSessionFailure ? "w-full rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 sm:w-auto sm:min-w-[11.5rem]" : "w-full rounded-xl border border-slate-700 px-5 py-2.5 text-sm font-semibold text-slate-200 transition hover:bg-slate-800 sm:w-auto sm:min-w-[10.5rem]"}
                    >
                      {isSessionFailure ? 'Continue to sign in' : (user?.uid && billingAccessGranted ? 'Open billing' : 'Return to sign in')}
                    </button>
                  </>
                ) : (
                  <div className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-300">
                    <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-400" />
                    Confirming access…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    if (publicPath === ('/beta' as PublicRoutePath) && tenantHostClass === 'base') {
      return <BetaRegistrationPage />;
    }

    if (fatalError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] text-slate-200 p-6">
          <div className="max-w-2xl w-full bg-slate-900/80 border border-slate-700/50 rounded-xl p-6">
            <div className="text-lg font-semibold text-white">Something went wrong while loading your workspace.</div>
            <div className="text-sm text-slate-300 mt-3 whitespace-pre-line">Please refresh the page.
If the problem continues, contact support.</div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  try { window.location.reload(); } catch {}
                }}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Refresh
              </button>
              <a
                href="/help#contact"
                className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      );
    }

    if (isWorkspaceLoaderBranch && user?.uid) {
      return (
        <WorkspaceLoadGuard
          key="workspace-loader-authenticated"
          title={startupReadiness.statusLabel}
          subtitle={
            renderBranch === 'billing_access_check_pending'
              ? 'Confirming access'
              : renderBranch === 'login_access_check_pending'
              ? 'Checking access'
              : 'Please wait while we get things ready'
          }
          showHeaderCopy
          progress={startupReadiness.progress}
          isReady={startupReadiness.routeReady}
          onComplete={() => {
            if (!hasNavigatedRef.current) {
              hasNavigatedRef.current = true;
              setDashboardCommitted(true);
              tpStripeFlashLog('workspace_ready', { correlationId: bootstrapCorrelationIdRef.current });
            }
          }}
          slowThresholdMs={WORKSPACE_SLOW_THRESHOLD_MS}
          attempt={authBootstrapAttempt}
          watch={{ authReady, tenantBootstrapReady, billingReady, userUid: user?.uid, checkoutReturnVisible: checkoutReturn.visible }}
          onRetry={retryWorkspaceBootstrap}
          onOpenAnyway={() => continueCheckoutReturnToWorkspace('timer')}
          error={authBootstrapError || tenantBootstrapError || billingError || null}
          isSlow={tenantBootstrapSlow}
          isAuthenticated
          onSignOut={handleTenantBootstrapSignOut}
          whiteLabel={whiteLabelAppBranding}
          neutralBrandFallback={tenantHostClass !== 'base' && whiteLabelAppBranding === null}
        />
      );
    }

    // Tenant-host surfaces: a tenant hostname must never render the Trade-Proof
    // marketing page. Unknown tenants get a controlled Tenant Not Found page;
    // hosts still resolving branding (or a known tenant at a marketing path) get a
    // neutral loading surface until the canonical redirect to /login takes effect.
    if (isTenantNotFoundHost) {
      return <TenantHostStatusPage hostname={tenantHostname} status="not_found" />;
    }
    if (
      tenantHostClass !== 'base'
      && isTenantMarketingPath
      && publicPath !== ('/login' as PublicRoutePath)
      && publicPath !== ('/signup' as PublicRoutePath)
      && (tenantBrandingPending || isTenantHost)
    ) {
      return <TenantHostStatusPage hostname={tenantHostname} status="loading" />;
    }

    // Branded public surfaces (login, signup, forgot/reset password, checkout
    // returns) on a tenant host must NEVER render with the default Trade-Proof
    // branding. While tenant branding is resolving — or when the public branding
    // payload is withheld (unpublished draft) — show a neutral status surface.
    // The auth callback is intentionally excluded: it is un-branded and must run
    // immediately so OAuth / email-confirmation exchanges are never delayed.
    if (
      tenantHostClass !== 'base'
      && publicPath !== ('/auth/callback' as PublicRoutePath)
      && (tenantBrandingPending || (isTenantHost && whiteLabelAppBranding === null))
    ) {
      return (
        <TenantHostStatusPage
          hostname={tenantHostname}
          status={tenantBrandingPending ? 'loading' : 'draft'}
        />
      );
    }

    // If auth is still initializing, avoid rendering a blank screen
    if (publicPath === ('/auth/callback' as PublicRoutePath)) {
      return <AuthCallbackPage />;
    }

    if (publicPath === ('/forgot-password' as PublicRoutePath)) {
      return <ForgotPasswordPage onBackToLogin={() => navigatePublic('/login')} />;
    }

    if (publicPath === ('/auth/reset-password' as PublicRoutePath)) {
      return <ResetPasswordPage onBackToLogin={() => navigatePublic('/login')} />;
    }

    if (isAlwaysPublicRoute) {
      return <PublicSiteShell path={publicSitePath} hash={publicLocation.hash} onNavigate={navigatePublic} />;
    }

    if (isLogoutTransitionActive) {
      return renderLoginPage(false);
    }

  if (!authReady) {
    if (isLogoutTransitionActive) {
      return renderLoginPage(false);
    }

    if (
      currentRuntime.brand.frontDoor !== 'login'
      && (
        (publicPath && publicPath !== ('/login' as PublicRoutePath) && publicPath !== ('/signup' as PublicRoutePath))
        || (livePublicPath && livePublicPath !== ('/login' as PublicRoutePath) && livePublicPath !== ('/signup' as PublicRoutePath))
      )
    ) {
      return <PublicSiteShell path={publicSitePath} hash={publicLocation.hash} onNavigate={navigatePublic} />;
    }

    if (checkoutReturn.visible) {
      return renderCheckoutReturnSurface();
    }

    if (isLoginRoute || currentRuntime.brand.frontDoor === 'login') {
      return renderLoginPage(false);
    }

    if (isWorkspaceLoaderBranch) {
      return null;
    }

    return (
      <WorkspaceTransitionSurface
        title={startupReadiness.statusLabel}
        subtitle="Please wait while we get things ready"
        showHeaderCopy
        progress={startupReadiness.progress}
        whiteLabel={whiteLabelAppBranding}
        neutralBrandFallback={tenantHostClass !== 'base' && whiteLabelAppBranding === null}
      />
    );
  }

  if (isAlwaysPublicRoute) {
    return <PublicSiteShell path={publicSitePath} hash={publicLocation.hash} onNavigate={navigatePublic} />;
  }

  if (checkoutReturn.visible) {
    return renderCheckoutReturnSurface();
  }

  if (isSignupCheckoutHandoffActive) {
    return renderLoginPage(true);
  }

  if (isSignupRoute) {
    return renderLoginPage(isSignupCheckoutHandoffActive);
  }

  if (loginAuthError) {
    return renderLoginPage(false);
  }

  if (isBillingCancelledReturn && isLoginRoute) {
    const branding = getBranding(currentRuntime.brand.id);
    return (
      <AbandonedCheckoutPage
        tenantName={resolveTenantProductName()}
        productName={branding.copy.billing.planName}
        monthlyPrice={branding.copy.billing.pricing.monthly.priceLabel}
        trialDays={branding.copy.billing.trialDays ?? 7}
        logoSrc={getAppLogoSrc({ brand: branding, variant: 'horizontalSplit', theme: 'dark' })}
        whiteLabel={whiteLabelAppBranding}
        isLoading={Boolean(user?.uid) && abandonedCheckoutLoading}
        tenantLandingUrl={abandonedCheckoutMainSiteUrl}
        onStartCheckout={user?.uid
          ? async () => {
            if (abandonedCheckoutLoading) return;
            setAbandonedCheckoutLoading(true);
            try {
              const checkoutUrl = await createCheckoutSession(
                { uid: String(user.uid), email: user?.email ? String(user.email) : null },
                { plan: 'monthly' },
              );
              window.location.href = checkoutUrl;
            } catch (err) {
              console.error('[AbandonedCheckoutCancelledReturn] checkout error', err);
              setAbandonedCheckoutLoading(false);
            }
          }
          : () => {
            clearSignupCheckoutHandoff('abandoned_checkout_unauthenticated');
            replaceCheckoutReturnLocation({ pathname: '/login', publicPath: '/login' as const, hash: null });
          }}
        onSignInToExistingAccount={() => {
          escapeToExistingAccountLogin('abandoned_checkout_existing_account');
        }}
      />
    );
  }

  if (renderBranch === 'login_surface') {
    return renderLoginPage(false);
  }

  if (!user && !isPublicGuest) {
    // Ensure direct pathname access to /login or /signup always shows the LoginPage
    try {
      const rawPathname = String(window.location.pathname || '/').trim() || '/';
      if (rawPathname === '/login' || rawPathname === '/signup') {
        return renderLoginPage(false);
      }
    } catch {
      // ignore any cross-origin or SSR edge cases
    }
    if (publicPath === ('/login' as PublicRoutePath) || publicPath === ('/signup' as PublicRoutePath)) {
      return renderLoginPage(false);
    }

    // Runtime-configured front doors let white-label tenants land on login
    // without hardcoding tenant checks into the shared app shell.
    if ((publicPath === ('/' as PublicRoutePath) || publicPath == null) && currentRuntime.brand.frontDoor === 'login') {
      return renderLoginPage(false);
    }

    if (currentRuntime.brand.frontDoor === 'login' && !isAlwaysPublicRoute) {
      return renderLoginPage(false);
    }

    return <PublicSiteShell path={publicSitePath} hash={publicLocation.hash} onNavigate={navigatePublic} />;
  }

  if (isAlwaysPublicRoute || (publicPath && (publicPath !== '/' || publicLocation.hash || publicLocation.explicit) && String(publicPath) !== '/login' && String(publicPath) !== '/signup' && currentRuntime.brand.frontDoor !== 'login')) {
    return <PublicSiteShell path={publicSitePath} hash={publicLocation.hash} onNavigate={navigatePublic} />;
  }

  if (user?.uid && !isAlwaysPublicRoute) {
    const pathname = String(window.location.pathname || '/').trim() || '/';
    if (pathname !== '/' && !resolveAuthenticatedPageFromPath(pathname) && publicPath == null) {
      return <Error404 onDashboard={() => { window.location.href = '/dashboard'; }} onBack={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/dashboard')} />;
    }
  }

  if (renderBranch === 'billing_gate' && user?.uid) {
    if (isReactivationState(billingState)) {
      const branding = getBranding(currentRuntime.brand.id);
      return (
        <ReactivateSubscriptionPage
          tenantName={resolveTenantProductName()}
          productName={branding.copy.billing.planName}
          monthlyPrice={branding.copy.billing.pricing.monthly.priceLabel}
          logoSrc={getAppLogoSrc({ brand: branding, variant: 'horizontalSplit', theme: 'dark' })}
          whiteLabel={whiteLabelAppBranding}
          isLoading={reactivateLoading}
          onReactivate={async () => {
            if (reactivateLoading) return;
            setReactivateLoading(true);
            try {
              const checkoutUrl = await createCheckoutSession(
                { uid: String(user.uid), email: user?.email ? String(user.email) : null },
                { plan: 'monthly' },
              );
              window.location.href = checkoutUrl;
            } catch (err) {
              console.error('[ReactivateSubscription] checkout error', err);
              setReactivateLoading(false);
            }
          }}
        />
      );
    }
    if (isAbandonedCheckoutState(billingState)) {
      const branding = getBranding(currentRuntime.brand.id);
      return (
        <AbandonedCheckoutPage
          tenantName={resolveTenantProductName()}
          productName={branding.copy.billing.planName}
          monthlyPrice={branding.copy.billing.pricing.monthly.priceLabel}
          trialDays={branding.copy.billing.trialDays ?? 7}
          logoSrc={getAppLogoSrc({ brand: branding, variant: 'horizontalSplit', theme: 'dark' })}
          whiteLabel={whiteLabelAppBranding}
          isLoading={abandonedCheckoutLoading}
          tenantLandingUrl={abandonedCheckoutMainSiteUrl}
          onSignInToExistingAccount={() => {
            escapeToExistingAccountLogin('billing_gate_existing_account');
          }}
          onStartCheckout={async () => {
            if (abandonedCheckoutLoading) return;
            setAbandonedCheckoutLoading(true);
            try {
              const checkoutUrl = await createCheckoutSession(
                { uid: String(user.uid), email: user?.email ? String(user.email) : null },
                { plan: 'monthly' },
              );
              window.location.href = checkoutUrl;
            } catch (err) {
              console.error('[AbandonedCheckout] checkout error', err);
              setAbandonedCheckoutLoading(false);
            }
          }}
        />
      );
    }
    return (
      <BillingAccessGate
        identity={{
          uid: String(user.uid),
          email: user?.email ? String(user.email) : null,
        }}
        billingState={billingState}
        onRefresh={refreshBillingState}
        tenantLandingUrl={abandonedCheckoutMainSiteUrl}
        onSignInToExistingAccount={() => {
          escapeToExistingAccountLogin('billing_gate_existing_account');
        }}
        whiteLabel={whiteLabelAppBranding}
      />
    );
  }



  if (BILLING_BYPASS_ACTIVE) {
    console.log('BILLING BYPASS ACTIVE');
  }

  const renderPage = () => {
    if (active === 'Journal' && viewingDate) {
      return (
        <JournalEntryPage
          date={viewingDate}
          onNavigateDate={handleNavigateToJournalEntry}
          isNewEntry={isNewEntry}
          onCreateNew={handleCreateNewJournalEntry}
        />
      );
    }

    switch (active) {
    case 'Dashboard':
      return (
        <DashboardPage
          theme={theme}
          onNavigateToJournalEntry={handleNavigateToJournalEntry}
          onOpenImportTrades={() => handleNavigate('Import Trades')}
          onNavigate={(page) => handleNavigate(page as PageName)}
          routeKey={active}
          hasTrades={authoritativeTrades.length > 0}
          onOpenSettings={(tabLabel) => {
            setSettingsInitialTabLabel(tabLabel);
            handleNavigate('Settings');
          }}
          billingState={billingState}
          billingReady={billingReady}
          layoutPresetsButtonRef={layoutPresetsButtonRef}
          dashboardUserKey={dashboardUserKey}
          isBootstrapping={isWorkspaceBootstrapPending}
          hasRealTrades={authoritativeTrades.length > 0}
          demoModePermanentlyDisabled={demoModePermanentlyDisabled}
          demoModeTemporarilyDisabled={demoModeTemporarilyDisabled}
          whiteLabelTenantSlug={whiteLabelAppBranding?.tenantSlug}
          whiteLabelTenantReady={tenantHostClass === 'base' || whiteLabelAppBranding !== null}
        />
      );
        case 'Calendar':
            return <CalendarPage
              onNavigateToJournalEntry={handleNavigateToJournalEntry}
              onCreateNewEntry={handleCreateNewJournalEntry}
            />;
        case 'Reports':
          return (
            <Trend
              selectedMarketType={selectedMarketType}
              onSelectedMarketTypeChange={setSelectedMarketType}
            />
          );
        case 'Win vs Loss Days':
          return (
            <WinVsLossDays
              selectedMarketType={selectedMarketType}
              onSelectedMarketTypeChange={setSelectedMarketType}
            />
          );
        case 'Performance Overview':
          return (
            <PerformanceOverview
              selectedMarketType={selectedMarketType}
              onSelectedMarketTypeChange={setSelectedMarketType}
            />
          );
        case 'Tag Patterns':
          return <TagPatterns theme={theme} />;
        case 'Trades':
          return <TradesPage />;
        case 'Trade Details': {
          const fallbackTrade: Trade | null = (() => {
            try {
              const raw = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
              if (!raw) return null;
              const parsed = JSON.parse(raw);
              return parsed as Trade;
            } catch {
              return null;
            }
          })();

          const resolvedTrade = selectedTrade ?? fallbackTrade;
          const tradeForDetails: ComponentProps<typeof TradeDetailsPage>['trade'] = resolvedTrade
            ? ({
                ...resolvedTrade,
                id: String((resolvedTrade as any).id),
              } as unknown as ComponentProps<typeof TradeDetailsPage>['trade'])
            : null;

          return (
            <TradeDetailsPage
              trade={tradeForDetails}
              onBack={handleBackFromTradeDetails}
            />
          );
        }
        case 'Journal':
          return <JournalPage theme={theme} />;
        case 'Search':
            return <SearchPage />;
        case 'New Trade':
            return <NewTradePage />;
        case 'Import Trades':
            return <ImportTradesPage onOpenDocumentation={() => handleNavigate('Support')} />;
          case 'Import Rejections' as PageName:
            return <RejectedImportsPage />;
          case 'Settings':
            return (
              <SettingsPage
                theme={theme}
                billingState={BILLING_BYPASS_ACTIVE ? null : billingState}
                billingReady={billingReady}
                whiteLabel={whiteLabelAppBranding}
                accountIdentity={user ? { uid: user.uid ?? null, displayName: user.displayName ?? null, email: user.email ?? null, createdAt: user.createdAt ?? user.created_at ?? null, lastLoginAt: user.lastLoginAt ?? user.last_sign_in_at ?? user.lastSignInAt ?? null } : null}
                onAvatarChange={setAvatarUrl}
                onDisplayNameChange={(nextDisplayName: string) => {
                  setUser((current: { displayName?: string | null } | null) => (current ? { ...current, displayName: nextDisplayName } : current));
                }}
                onThemeChange={tenantThemeToggle}
                adminToolsAccess={adminToolsAccess}
                adminToolsAccessReady={adminToolsAccessReady}
                adminToolsAccessError={adminToolsAccessError}
                onRetryAdminToolsAccess={() => { void refreshAdminToolsAccess(); }}
                initialTabLabel={settingsInitialTabLabel}
                onTabChange={() => setSettingsInitialTabLabel(undefined)}
              />
            );
            case 'Admin Tools':
              if (adminToolsAccessReady && !showAdminToolsNav) {
                return null;
              }
              return (
                <AdminToolsPage
                  theme={theme}
                  accountIdentity={user ? { uid: user.uid ?? null, email: user.email ?? null } : null}
                  accessState={adminToolsAccess}
                  accessReady={adminToolsAccessReady}
                  accessError={adminToolsAccessError}
                  onRetryAccess={() => { void refreshAdminToolsAccess(); }}
                  onNavigate={handleNavigate}
                />
              );
          case 'Support Tickets':
            return (
              <SupportTicketsPage
                theme={theme}
                accountIdentity={user ? { uid: user.uid ?? null, email: user.email ?? null } : null}
              />
            );
          case 'Support':
            return <SupportPage whiteLabel={whiteLabelAppBranding} accountContext={user ? { uid: user.uid ?? null, email: user.email ?? null } : null} />;
          case 'Business':
            // Direct URL access is protected server-side; while the check is
            // pending we render nothing (the effect redirects / showBusinessNav
            // false renders empty), and unauthorized users never see the page.
            if (businessAccessReady && !showBusinessNav) return null;
            return (
              <BusinessPage
                accountIdentity={user ? { uid: user.uid ?? null, email: user.email ?? null } : null}
              />
            );
          case 'Challenges':
            if (currentRuntime.brand.id !== 'warrior-trading') {
              return (
                <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
                  <h1 className="text-2xl font-bold">Not Found</h1>
                  <p className="text-zinc-600 dark:text-zinc-400 mt-2">The requested page does not exist or you do not have access.</p>
                </div>
              );
            }
            return <Challenges theme={theme} isAdmin={canManageChallengeVideos} />;
        default:
            return (
                <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
                    <h1 className="text-2xl font-bold">{active}</h1>
                    <p className="text-zinc-600 dark:text-zinc-400 mt-2">Placeholder page for the {active} section.</p>
                </div>
            );
    }
  };

  const renderPageWithSuspense = () => (
    <>
      {tradeHistoryStatus.phase === 'loading' && (
        <div role="status" className="mx-4 mb-3 rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm text-blue-700 dark:text-blue-200">
          Syncing complete trade history ({tradeHistoryStatus.loaded.toLocaleString()}{tradeHistoryStatus.total == null ? '' : ` of ${tradeHistoryStatus.total.toLocaleString()}`}). Analytics update as older batches arrive.
        </div>
      )}
      {tradeHistoryStatus.phase === 'error' && (
        <div role="alert" className="mx-4 mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-200">
          Trade history synchronization paused ({tradeHistoryStatus.loaded.toLocaleString()} loaded). Analytics will update upon resume.{' '}
          <button type="button" className="underline font-semibold" onClick={requestPersistedTradeHistoryRetry}>Retry</button>
        </div>
      )}
      <Suspense fallback={<div className="flex min-h-[12rem] items-center justify-center text-sm text-slate-500 dark:text-slate-400">Loading workspace…</div>}>
        {renderPage()}
      </Suspense>
    </>
  );

  return (
    <WorkspaceViewportGate
      onSignOut={beginLogoutTransition}
      onNavigateHome={() => navigatePublic('/')}
    >
      <div className="w-full max-w-none min-h-screen min-w-0 bg-transparent dark:bg-transparent text-zinc-900 dark:text-zinc-100 flex">
      {/* Dev billing bypass notice & modal: visible only when billing is not granted and enforcement is disabled */}
      {/* Dev billing bypass banner and overlay removed (dev-only debug UI) */}
      {navLayout === 'top' ? (
        <div className="w-full max-w-none flex-1 min-w-0 flex flex-col min-h-0">
          {/* Normalize active page for top-level nav so Trends (Reports) stays highlighted
              when any Trends subpage is active. */}
          {/* Map detailed pages that belong under the Reports/Trends section back to 'Reports' */}
          {(() => {
            const navActive = (['Reports', 'Performance Overview', 'Win vs Loss Days', 'Tag Patterns'] as string[]).includes(active) ? 'Reports' : active;
            return (
              <HorizontalMenu
                active={navActive}
                onNavigate={handleNavigate as unknown as (p: string) => void}
                onLogoutStart={beginLogoutTransition}
                avatarUrl={avatarUrl}
                displayName={user?.displayName ?? null}
                email={user?.email ?? null}
                locationLabel={getTimezoneFallbackLabel()}
                showAdminTools={showAdminToolsNav}
                showBusiness={showBusinessNav}
                navLayout={navLayout}
                setNavLayout={navigationLayoutMode === 'both' ? setNavLayout : undefined}
                theme={theme}
                setTheme={tenantThemeToggle}
                canSwitchTheme={tenantThemeMode === 'light_dark'}
                layoutPresetsButtonRef={layoutPresetsButtonRef}
                containerSpacingClassName={isSettingsRoute || isAnalyticsRoute || isSupportRoute || isDashboardRoute || isImportTradesRoute ? 'mb-0' : undefined}
                hasBottomBorder={(!isSettingsRoute && !isAnalyticsRoute) || theme === 'dark'}
                whiteLabel={whiteLabelAppBranding}
              />
            );
          })()}
          {active === 'Trade Details' ? (
            <div className="w-full px-6 pt-[10px] pb-2">
              <div>
                <button
                  type="button"
                  onClick={handleBackFromTradeDetails}
                  className={theme === 'dark' ? "inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm border border-gray-800 bg-[#1a1a1c] text-gray-100 hover:bg-[#222226] transition-colors shadow-md" : "inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors shadow-sm"}
                >
                  Back to Trades
                </button>
              </div>
            </div>
          ) : null}
          <main
            ref={mainRef}
            className={isSettingsRoute
              ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0`
              : isAnalyticsRoute
                ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0 bg-zinc-100 dark:bg-zinc-950`
              : isSupportRoute
                  ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0`
                  : isDashboardRoute
                    ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0`
                    : isImportTradesRoute
                      ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0 bg-zinc-100 dark:bg-zinc-950`
                    : active === 'Journal'
                            ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0 bg-zinc-100 dark:bg-zinc-900`
                    : active === 'Admin Tools'
                      ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-0 overflow-auto min-h-0 bg-[#f5f5f5] dark:bg-transparent`
                    : active === 'Trades'
                      ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-3 overflow-visible min-h-0`
                    : `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 pt-3 overflow-auto min-h-0`}
          >
            {currentRuntime.tenant.id === 'trade-proof' && active !== 'Journal' && active !== 'Trade Details' && (
              <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-x-0 top-[5.5rem] z-[1001] hidden h-[18px] bg-[var(--page-surface-bg,hsl(var(--background)))] md:block"
              />
            )}
            {active === 'Journal' && (
              <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-x-0 top-[5.5rem] z-[1001] hidden h-[18px] md:grid md:grid-cols-[var(--journal-left-width,320px)_minmax(0,1fr)_var(--journal-right-width,336px)]"
              >
                <div className="border-r border-zinc-200/70 bg-zinc-100 dark:border-zinc-800/60 dark:bg-[#1d1d20]" />
                <div className="border-r border-zinc-200/70 bg-zinc-100 dark:border-zinc-800/60 dark:bg-zinc-900" />
                <div className="bg-zinc-100 dark:bg-[#1d1d20]" />
              </div>
            )}
            {isAnalyticsRoute ? (
              <div className="w-full max-w-none px-0 pb-0 pt-0">
                {renderPageWithSuspense()}
              </div>
            ) : renderPageWithSuspense()}
          </main>
        </div>
      ) : (
        <>
          {/* Sidebar should also treat Trends sub-pages as part of Reports for highlighting */}
          <Sidebar
            active={( ['Reports', 'Performance Overview', 'Win vs Loss Days', 'Tag Patterns'] as string[]).includes(active) ? 'Reports' : active}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            onNavigate={handleNavigate}
            onLogoutStart={beginLogoutTransition}
            displayName={user?.displayName ?? null}
            email={user?.email ?? null}
            locationLabel={user?.locationLabel ?? getTimezoneFallbackLabel()}
            theme={theme}
            setTheme={tenantThemeToggle}
            canSwitchTheme={tenantThemeMode === 'light_dark'}
            avatarUrl={avatarUrl}
            showAdminTools={showAdminToolsNav}
            showBusiness={showBusinessNav}
            whiteLabel={whiteLabelAppBranding}
            setNavLayout={navigationLayoutMode === 'both' ? setNavLayout : undefined}
          />
          <main
            ref={mainRef}
            className={isSettingsRoute
              ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-auto min-h-0 relative bg-zinc-100 dark:bg-zinc-950`
              : isAnalyticsRoute
                ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-auto min-h-0 relative bg-zinc-100 dark:bg-zinc-950`
                : active === 'Journal'
                  ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-auto min-h-0 relative bg-zinc-100 dark:bg-zinc-900`
                : active === 'Admin Tools'
                  ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-auto min-h-0 relative bg-[#f5f5f5] dark:bg-transparent`
                : active === 'Trades'
                    ? `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-visible min-h-0 relative bg-zinc-100 dark:bg-zinc-950`
                      : `w-full max-w-none flex-1 min-w-0 ${shellHorizontalPaddingClassName} pb-0 ${sideNavTopSpacingClassName} overflow-auto min-h-0 relative bg-zinc-100 dark:bg-zinc-950`}
          >
            {isPublicGuest && (
              <GuestModeHeaderIndicator
                onOpenSignupModal={() => triggerGuestRestrictedAction('Registration')}
                isDark={theme === 'dark'}
              />
            )}
            {active === 'Dashboard' && String(navLayout) !== 'top' && (
              <div className="absolute top-8 right-6 z-10">
                <div className="flex items-center justify-end gap-2">
                  <LayoutPresetsMenu
                    menuButtonRef={layoutPresetsButtonRef}
                    dashboardUserKey={dashboardUserKey}
                    className="!relative !right-auto !top-auto !z-auto shrink-0"
                  />
                  {showDemoUi && !demoModePermanentlyDisabled ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (demoMode.enabled) {
                          window.dispatchEvent(new CustomEvent('tradeproof:request-exit-demo'));
                        } else {
                          enableDemoMode();
                          setDemoModeTemporarilyDisabledState(false);
                          setDemoModeTemporarilyDisabled(false);
                        }
                      }}
                      className={`relative z-10 inline-flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors shadow-sm ${demoMode.enabled
                        ? 'border-[#9d61fc] bg-amber-50 text-[#9d61fc] hover:bg-amber-100 dark:border-[#9d61fc] dark:bg-[#9d61fc]/50 dark:text-amber-100 dark:hover:bg-[#9d61fc]'
                        : 'border-amber-300/70 bg-[#ad5605] text-white hover:bg-[#ad5605] dark:border-amber-300/40 dark:bg-[#ad5605] dark:text-white dark:hover:bg-[#ad5605]'
                      }`}
                    >
                      <Sparkles className="h-4 w-4" />
                      {demoMode.enabled ? 'Exit Demo' : 'Try Demo'}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {isAnalyticsRoute ? (
              <div className="w-full max-w-none px-0 pb-0 pt-0">
                <div className={sidePanelContentShellClassName}>
                  {renderPageWithSuspense()}
                </div>
              </div>
            ) : (
              <div className={sidePanelContentShellClassName}>
                {renderPageWithSuspense()}
              </div>
            )}
          </main>
        </>
      )}

      <CreateCustomEntryModal
        open={createCustomOpen}
        onClose={() => setCreateCustomOpen(false)}
        onCreate={(payload) => {
          console.log('Created custom entry from main menu', payload);
          setCreateCustomOpen(false);
        }}
      />
      {dashboardUserKey !== 'guest' ? (
        <OnboardingTour
          theme={theme}
          brandId={currentRuntime.brand.id as 'trade-proof' | 'warrior-trading'}
          tenantSlug={whiteLabelAppBranding?.tenantSlug}
          primaryColor={whiteLabelAppBranding?.primaryColor}
          userId={dashboardUserKey}
          autoStart
          onConnectBroker={() => {
            setSettingsInitialTabLabel('CHARTING SETUP');
            handleNavigate('Settings');
          }}
          onNavigate={(page) => handleNavigate(page as PageName)}
          routeKey={active}
          hasTrades={authoritativeTrades.length > 0}
        />
      ) : null}
      {firstSessionOnboardingVisible ? (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-950/70 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-slate-950/95 p-6 text-slate-100 shadow-[0_24px_90px_rgba(2,6,23,0.55)]">
            <div className="space-y-3 text-center">
              <div className="inline-flex items-center rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-200">
                First trade review
              </div>
              <h2 className="text-3xl font-semibold tracking-tight">Let’s get your first trade in</h2>
              <p className="text-sm leading-6 text-slate-300">Import your trades to start replaying and analyzing your performance.</p>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={handleFirstSessionImportTrades}
                className="inline-flex items-center justify-center rounded-2xl bg-sky-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-400"
              >
                Import trades (CSV / Broker)
              </button>
              {showDemoUi ? (
                <button
                  type="button"
                  onClick={handleFirstSessionDemoTrade}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-700 bg-slate-900/80 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-slate-800"
                >
                  Try a demo trade
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <GlobalToast />
      </div>
      <BetaFeedbackWidget tenantId={activeTenantId} theme={theme} />
    </WorkspaceViewportGate>
    );
  };

  return (
    <DemoTradesOverrideProvider trades={demoTradesActive ? demoTrades : null}>
      {renderMainContent()}
      <ExitDemoConfirmationModal
        isOpen={isExitDemoModalOpen}
        onClose={() => setIsExitDemoModalOpen(false)}
        onConfirmPermanent={handleConfirmExitPermanent}
        onConfirmTemporary={handleConfirmExitTemporary}
        isDark={theme === 'dark'}
      />
      <GuestSignupModal
        isOpen={isGuestSignupModalOpen}
        featureName={guestModalFeatureName}
        onClose={() => setIsGuestSignupModalOpen(false)}
        onSignup={() => {
          setIsGuestSignupModalOpen(false);
          clearPublicGuestSession();
          navigatePublic('/signup' as PublicRoutePath);
        }}
        isDark={theme === 'dark'}
      />
      {isWorkspaceLoaderBranch && !user?.uid && (
        <WorkspaceLoadGuard
          key="workspace-loader"
          title={startupReadiness.statusLabel}
          subtitle={
            renderBranch === 'billing_access_check_pending'
              ? 'Confirming access'
              : renderBranch === 'login_access_check_pending'
              ? 'Checking access'
              : 'Please wait while we get things ready'
          }
          showHeaderCopy
          progress={startupReadiness.progress}
          isReady={startupReadiness.routeReady}
          onComplete={() => {
            if (!hasNavigatedRef.current) {
              hasNavigatedRef.current = true;
              setDashboardCommitted(true);
              tpStripeFlashLog('workspace_ready', { correlationId: bootstrapCorrelationIdRef.current });
            }
          }}
          slowThresholdMs={WORKSPACE_SLOW_THRESHOLD_MS}
          attempt={authBootstrapAttempt}
          watch={{ authReady, tenantBootstrapReady, billingReady, userUid: user?.uid, checkoutReturnVisible: checkoutReturn.visible }}
          onRetry={retryWorkspaceBootstrap}
          onOpenAnyway={() => continueCheckoutReturnToWorkspace('timer')}
          error={authBootstrapError || tenantBootstrapError || billingError || null}
          isSlow={tenantBootstrapSlow}
          isAuthenticated={Boolean(user?.uid)}
          onSignOut={handleTenantBootstrapSignOut}
          whiteLabel={whiteLabelAppBranding}
          neutralBrandFallback={tenantHostClass !== 'base' && whiteLabelAppBranding === null}
        />
      )}
    </DemoTradesOverrideProvider>
  );
}
